import React, { useEffect, useMemo, useState } from "react";
import { fetchDashboard } from "./api/finopsApi";
import { fetchAiSummary } from "./api/aiApi";
import { fetchAiAssistant } from "./api/aiAssistantApi";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "./firebase";
import Login from "./Login";
import MFA from "./pages/MFA";
import MfaVerify from "./pages/MfaVerify";
import { doc, getDocFromServer, setDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import Pricing from "./pages/Pricing";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  AreaChart,
  Area
} from "recharts";
import jsPDF from "jspdf";

const API_BASE = "https://tansmartx-api-446662168422.asia-southeast1.run.app";
const TBM_API_BASE = "https://tansmartx-tbm-api-446662168422.asia-southeast1.run.app";
const projectId = "my-firstproject-350605";

const COLORS = {
  blue: "#2563EB",
  green: "#16A34A",
  amber: "#F59E0B",
  purple: "#7C3AED",
  red: "#DC2626",
  cyan: "#0891B2",
  slate: "#64748B",
  dark: "#020617",
  card: "#0B1220",
  border: "rgba(148,163,184,0.18)",
};

const resourceColors = [
  COLORS.blue,
  COLORS.green,
  COLORS.amber,
  COLORS.purple,
  COLORS.red,
  COLORS.slate,
  COLORS.cyan,
  "#94A3B8",
];

const cloudColors = {
  GCP: COLORS.blue,
  AWS: COLORS.amber,
  Azure: "#0EA5E9",
};

const cardStyle = {
  background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(8,13,24,0.98))",
  border: `1px solid ${COLORS.border}`,
  borderRadius: "20px",
  boxShadow: "0 12px 32px rgba(0,0,0,0.26)",
};

const PLAN_RANK = {
  basic: 1,
  standard: 2,
  enterprise: 3,
};

const PLAN_LABEL = {
  basic: "Basic",
  standard: "Standard",
  enterprise: "Enterprise",
};

function isPlanAtLeast(currentPlan, requiredPlan) {
  return (PLAN_RANK[currentPlan] || 1) >= (PLAN_RANK[requiredPlan] || 1);
}

function getResourceColor(index) {
  return resourceColors[index % resourceColors.length];
}

const DEFAULT_FX_RATES = {
  USD: 1,
  MYR: 4.7,
  SGD: 1.35,
  AUD: 1.52,
};

const CURRENCY_META = {
  USD: { label: "USD", name: "US Dollar", symbol: "$" },
  MYR: { label: "MYR", name: "Malaysian Ringgit", symbol: "RM" },
  SGD: { label: "SGD", name: "Singapore Dollar", symbol: "S$" },
  AUD: { label: "AUD", name: "Australian Dollar", symbol: "A$" },
};

function convertAmount(amount, fromCurrency, toCurrency, fxRates = DEFAULT_FX_RATES) {
  const fromRate = fxRates[fromCurrency] || 1;
  const toRate = fxRates[toCurrency] || 1;
  const usdValue = Number(amount || 0) / fromRate;
  return usdValue * toRate;
}

function formatMoney(currency, amount, decimals = 2) {
  const meta = CURRENCY_META[currency] || { symbol: currency };
  return `${meta.symbol} ${Number(amount || 0).toFixed(decimals)} ${currency}`;
}

function formatDateTime() {
  return new Date().toLocaleString();
}

function normalizeName(value, fallback = "Unknown") {
  return value || fallback;
}

function formatDaysToExceed(days) {
  if (days === null || days === undefined) return "Safe";
  if (days > 365) return "Low spend";
  if (days > 90) return "Very safe";
  if (days > 30) return `${Math.floor(days / 30)} months`;
  if (days > 7) return `${days} days`;
  if (days > 0) return "Risk soon";
  return "Exceeded";
}

function detectAnomaly(trend = []) {
  const MIN_SPIKE_AMOUNT = 1;
  const MIN_SPIKE_PERCENT = 50;

  if (!trend || trend.length < 2) {
    return {
      status: "Normal",
      level: "Low",
      message: "Not enough billing trend data to detect anomalies yet.",
      spikePercent: 0,
    };
  }

  const latest = Number(trend[trend.length - 1]?.cost || 0);
  const previous = Number(trend[trend.length - 2]?.cost || 0);
  const increaseAmount = latest - previous;

  if (increaseAmount < MIN_SPIKE_AMOUNT) {
    return {
      status: "Normal",
      level: "Low",
      message: `No meaningful spike detected. Latest increase is below USD ${MIN_SPIKE_AMOUNT}.`,
      spikePercent: 0,
    };
  }

  if (previous === 0 && latest > 0) {
    return {
      status: "Watch",
      level: "Medium",
      message: `New cloud cost appeared with USD ${latest.toFixed(4)} usage.`,
      spikePercent: 100,
    };
  }

  const spikePercent = ((latest - previous) / previous) * 100;

  if (spikePercent >= MIN_SPIKE_PERCENT) {
    return {
      status: "Spike Detected",
      level: "High",
      message: `Cost increased by ${spikePercent.toFixed(1)}%, with an increase of USD ${increaseAmount.toFixed(4)}.`,
      spikePercent,
    };
  }

  return {
    status: "Normal",
    level: "Low",
    message: "No meaningful cost spike detected.",
    spikePercent: 0,
  };
}

function buildBudgetForecast(actualBillingSpend, monthlyBudget) {
  const today = new Date();
  const daysPassed = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const avgDailySpend = daysPassed > 0 ? actualBillingSpend / daysPassed : 0;
  const projectedMonthEndSpend = avgDailySpend * daysInMonth;

  let daysToExceed = null;
  if (avgDailySpend > 0 && monthlyBudget > actualBillingSpend) {
    daysToExceed = Math.floor((monthlyBudget - actualBillingSpend) / avgDailySpend);
  }

  return {
    avgDailySpend,
    projectedMonthEndSpend,
    daysToExceed,
    forecastStatus: projectedMonthEndSpend > monthlyBudget ? "Will Exceed" : "Within Budget",
  };
}

function buildAiAnomalyExplanation(provider, anomaly, costDrivers, resources, currency, spend) {
  const topDriver = costDrivers?.[0];
  const topResource = resources?.[0];

  if (anomaly.status === "Normal") {
    return {
      cause: "No meaningful cost spike is detected from the available billing trend.",
      impact: `Current detected spend is ${currency} ${spend.toFixed(4)}.`,
      action: "Continue monitoring daily billing trend, enforce budget alerts, and review unused assets weekly.",
    };
  }

  let cause = "The spike may be caused by new usage, newly created resources, or delayed billing ingestion.";
  let action = "Review recent deployments, newly created resources, and high-cost services.";

  if (topDriver?.service || topDriver?.name) {
    cause = `The most likely cost driver is ${topDriver.service || topDriver.name}.`;
    action = `Check ${topDriver.service || topDriver.name} usage first, validate ownership, then confirm whether spend was expected.`;
  }

  if (topResource?.name) {
    cause += ` Resource-level data also shows ${topResource.name} as a priority resource to review.`;
  }

  return {
    cause,
    impact: `${provider.toUpperCase()} anomaly level is ${anomaly.level}. Spike change is ${anomaly.spikePercent.toFixed(1)}%.`,
    action,
  };
}

function buildSavingsOpportunities(resources = [], costDrivers = []) {
  const opportunities = [];
  const topDriver = costDrivers[0];

  if (topDriver?.service || topDriver?.name) {
    opportunities.push({
      id: `driver-${topDriver.service || topDriver.name}`,
      title: `Review ${topDriver.service || topDriver.name} usage`,
      category: "Cost Driver",
      impact: "High",
      owner: "Cloud Platform",
      effort: "Medium",
      estimatedSavings: Number(topDriver.cost || 0) * 0.15,
      recommendation: "Validate whether this usage is expected, identify linked resources, and check rightsizing or scheduling opportunities.",
    });
  }

  resources.slice(0, 5).forEach((resource, index) => {
    opportunities.push({
      id: `resource-${index}`,
      title: `Optimize ${resource.name || "resource"}`,
      category: resource.resourceType || "Resource",
      impact: Number(resource.estimatedMonthlyCost || 0) > 10 ? "High" : "Medium",
      owner: resource.owner || resource.tags?.owner || resource.labels?.owner || "Unassigned",
      effort: Number(resource.estimatedMonthlyCost || 0) > 10 ? "Medium" : "Low",
      estimatedSavings: Number(resource.estimatedMonthlyCost || 0) * 0.2,
      recommendation: "Review ownership, utilization, lifecycle, scheduling policy, and tag quality.",
    });
  });

  if (!opportunities.length) {
    return [
      {
        id: "visibility",
        title: "Enable deeper resource discovery",
        category: "Visibility",
        impact: "Medium",
        owner: "Cloud Platform",
        effort: "Low",
        estimatedSavings: 0,
        recommendation: "Connect detailed resource metadata to identify idle, oversized, untagged, and orphaned resources.",
      },
    ];
  }

  return opportunities;
}

function buildTagGovernance(resources = []) {
  let tagged = 0;
  let untagged = 0;
  const teamMap = {};
  const envMap = {};

  resources.forEach((r) => {
    const cost = Number(r.estimatedMonthlyCost || 0);
    const team = r.tags?.team || r.labels?.team || r.tags?.owner || r.labels?.owner || r.owner || "Unknown";
    const env = r.tags?.environment || r.labels?.environment || r.tags?.env || r.labels?.env || r.environment || "Unknown";

    if (team === "Unknown" || env === "Unknown") untagged++;
    else tagged++;

    teamMap[team] = (teamMap[team] || 0) + cost;
    envMap[env] = (envMap[env] || 0) + cost;
  });

  return {
    teamData: Object.entries(teamMap).map(([name, value]) => ({ name, value })),
    envData: Object.entries(envMap).map(([name, value]) => ({ name, value })),
    score: tagged + untagged === 0 ? 0 : (tagged / (tagged + untagged)) * 100,
    tagged,
    untagged,
  };
}

function buildBusinessAllocation(resources = []) {
  const businessMap = {};
  const categoryMap = {};
  const trendMap = {};

  resources.forEach((r) => {
    const cost = Number(r.estimatedMonthlyCost || 0);
    const business = r.tags?.business || r.labels?.business || r.tags?.team || "Unknown";
    const category = r.resourceType || r.tags?.category || "Other";
    const date = new Date().toLocaleDateString();

    businessMap[business] = (businessMap[business] || 0) + cost;
    categoryMap[category] = (categoryMap[category] || 0) + cost;
    if (!trendMap[date]) trendMap[date] = {};
    trendMap[date][category] = (trendMap[date][category] || 0) + cost;
  });

  return {
    businessKPI: Object.entries(businessMap).map(([name, value]) => ({ name, value })),
    categoryData: Object.entries(categoryMap).map(([name, value]) => ({ name, value })),
    trendData: Object.entries(trendMap).map(([date, categories]) => ({ date, ...categories })),
  };
}

function Badge({ children, color = COLORS.cyan }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "999px",
        background: `${color}22`,
        border: `1px solid ${color}55`,
        color,
        fontSize: "12px",
        fontWeight: 800,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function StatCard({ title, value, subtext, accent = COLORS.cyan, action }) {
  return (
    <div className="tx-card-hover" style={{ ...cardStyle, padding: "22px", position: "relative", overflow: "hidden", minHeight: "164px" }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "150px",
          height: "150px",
          background: `radial-gradient(circle, ${accent}33, transparent 66%)`,
        }}
      />
      <div style={{ color: "#94a3b8", fontSize: "13px", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.4px" }}>{title}</div>
      <div style={{ color: "white", fontSize: "34px", fontWeight: 950, marginTop: "14px", lineHeight: 1.1 }}>{value}</div>
      <div style={{ color: "#cbd5e1", marginTop: "12px", fontSize: "14px", lineHeight: 1.5 }}>{subtext}</div>
      {action && <div style={{ marginTop: "14px" }}>{action}</div>}
    </div>
  );
}

function SectionCard({ title, children, badge, right }) {
  return (
    <div className="tx-card-soft" style={{ ...cardStyle, padding: "24px", marginTop: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, color: "white", fontSize: "22px" }}>{title}</h2>
          {badge}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function DashboardWidget({ title, children, badge, minHeight = "280px" }) {
  return (
    <div className="tx-card-hover" style={{ ...cardStyle, padding: "20px", minHeight }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
        <div style={{ color: "white", fontWeight: 950, fontSize: "18px" }}>{title}</div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function ActionButton({ children, onClick, variant = "secondary", disabled = false }) {
  return (
    <button className="tx-button-hover" onClick={onClick} disabled={disabled} style={variant === "primary" ? primaryButton(disabled) : secondaryButton(disabled)}>
      {children}
    </button>
  );
}

function ApptioTabs({ activeTab, setActiveTab }) {
  const tabs = [
    { id: "executive", label: "Executive" },
    { id: "finance", label: "Finance" },
    { id: "engineering", label: "Engineering" },
    { id: "actions", label: "Action Center" },
    { id: "tbm", label: "TBM View" },
  ];

  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", background: "#020617", padding: "8px", borderRadius: "16px", border: "1px solid #1e293b" }}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          style={{
            border: "none",
            borderRadius: "12px",
            padding: "10px 14px",
            cursor: "pointer",
            fontWeight: 900,
            color: activeTab === tab.id ? "white" : "#94a3b8",
            background: activeTab === tab.id ? COLORS.blue : "transparent",
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}


function BrandMark({ size = 58 }) {
  return (
    <img
      src="/logo-icon.png"
      alt="Tanvoracloud"
      style={{
        height: size,
        width: "auto",
        minWidth: size,
        objectFit: "contain",
        display: "block",
        opacity: 1,
        filter: "brightness(1.32) contrast(1.22) saturate(1.15) drop-shadow(0 0 22px rgba(14,165,233,0.78))",
        transform: "scale(1.08)",
      }}
    />
  );
}

function BrandLockup({ size = 58, titleSize = 28, compact = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: compact ? "14px" : "18px" }}>
      <BrandMark size={size} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: compact ? "6px" : "9px",
            fontWeight: 950,
            fontSize: titleSize,
            lineHeight: 1,
            letterSpacing: "-0.7px",
            whiteSpace: "nowrap",
            textShadow: "0 10px 30px rgba(0,0,0,0.38)",
          }}
        >
          <span style={{ color: "#FFFFFF" }}>TanSmartX</span>
          <span
            style={{
              background: "linear-gradient(90deg, #A5F3FC 0%, #22D3EE 45%, #38BDF8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 0 16px rgba(56,189,248,0.38))",
            }}
          >
            FinOps
          </span>
        </div>
        <div
          style={{
            color: compact ? "#FFFFFF" : "#BAE6FD",
            fontSize: compact ? "13px" : "15px",
            fontWeight: 850,
            marginTop: compact ? "6px" : "8px",
            letterSpacing: "0.2px",
          }}
        >
          by Tanvoracloud
        </div>
      </div>
    </div>
  );
}

function UsageDrawer({ selectedDriver, onClose, displayCurrency, fxRates, resources, trend, provider }) {
  if (!selectedDriver) return null;

  const driverName = selectedDriver.service || selectedDriver.name || "Selected service";
  const driverCost = Number(selectedDriver.cost || 0);
  const relatedResources = resources.filter((r) => {
    const text = `${r.name || ""} ${r.resourceType || ""} ${r.service || ""}`.toLowerCase();
    return text.includes(driverName.toLowerCase()) || resources.length <= 5;
  });

  const usageTrend = trend.length
    ? trend.map((item) => ({ label: item.label || item.date, cost: convertAmount(item.cost || 0, "USD", displayCurrency, fxRates) }))
    : [
        { label: "Week 1", cost: driverCost * 0.2 },
        { label: "Week 2", cost: driverCost * 0.35 },
        { label: "Week 3", cost: driverCost * 0.55 },
        { label: "Current", cost: driverCost },
      ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(2,6,23,0.78)",
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(720px, 96vw)",
          height: "100%",
          background: "#020617",
          borderLeft: "1px solid #334155",
          boxShadow: "-20px 0 60px rgba(0,0,0,0.42)",
          padding: "26px",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "flex-start" }}>
          <div>
            <Badge color={COLORS.purple}>Usage Drill-down</Badge>
            <h2 style={{ color: "white", fontSize: "30px", margin: "14px 0 8px" }}>{driverName}</h2>
            <div style={{ color: "#94a3b8" }}>{provider.toUpperCase()} service-level cost investigation workspace</div>
          </div>
          <button onClick={onClose} style={closeButton()}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginTop: "22px" }}>
          <MiniPanel label="Detected Spend" value={formatMoney(displayCurrency, convertAmount(driverCost, "USD", displayCurrency, fxRates), 4)} color={COLORS.blue} />
          <MiniPanel label="Optimization Potential" value={formatMoney(displayCurrency, convertAmount(driverCost * 0.15, "USD", displayCurrency, fxRates), 4)} color={COLORS.green} />
          <MiniPanel label="Priority" value={driverCost > 10 ? "High" : driverCost > 1 ? "Medium" : "Low"} color={driverCost > 10 ? COLORS.red : driverCost > 1 ? COLORS.amber : COLORS.green} />
        </div>

        <SectionCard title="Usage Trend" badge={<Badge color={COLORS.blue}>Service View</Badge>}>
          <div style={{ height: "260px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={usageTrend}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={tooltipStyle()} />
                <Area type="monotone" dataKey="cost" name="Cost" stroke={COLORS.blue} fill={`${COLORS.blue}33`} strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Recommended FinOps Actions" badge={<Badge color={COLORS.green}>Next Best Action</Badge>}>
          <div style={{ display: "grid", gap: "12px" }}>
            <ActionRow title="Validate ownership" description="Confirm business owner, technical owner, environment, and cost center tags." impact="High" />
            <ActionRow title="Check utilization" description="Compare usage trend with expected workload pattern and identify idle or oversized services." impact="High" />
            <ActionRow title="Apply optimization" description="Use rightsizing, scheduling, committed-use discounts, savings plans, lifecycle policies, or reserved capacity where applicable." impact="Medium" />
          </div>
        </SectionCard>

        <SectionCard title="Related Resources" badge={<Badge color={COLORS.amber}>{relatedResources.length} assets</Badge>}>
          {relatedResources.length === 0 ? (
            <div style={{ color: "#94a3b8" }}>No related resource metadata available yet.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ color: "#94a3b8", textAlign: "left", fontSize: "13px" }}>
                    <th style={thStyle()}>Resource</th>
                    <th style={thStyle()}>Type</th>
                    <th style={thStyle()}>Region</th>
                    <th style={thStyle()}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedResources.slice(0, 8).map((r, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #1e293b" }}>
                      <td style={tdStyle()}>{r.name || `Resource ${i + 1}`}</td>
                      <td style={tdStyle()}>{r.resourceType || "Unknown"}</td>
                      <td style={tdStyle()}>{r.region || "N/A"}</td>
                      <td style={{ ...tdStyle(), color: "#93C5FD", fontWeight: 900 }}>
                        {formatMoney(displayCurrency, convertAmount(Number(r.estimatedMonthlyCost || 0), "USD", displayCurrency, fxRates), 4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function GuidedAssistant({ assistantLoading, assistantAnswer, handleAssistant }) {
  return (
    <SectionCard title="Guided AI Assistant" badge={<Badge color={COLORS.purple}>AI FinOps</Badge>}>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
        <ActionButton onClick={() => handleAssistant("explain_cost")}>Explain My Cost</ActionButton>
        <ActionButton onClick={() => handleAssistant("optimize")}>What Should I Optimize?</ActionButton>
        <ActionButton onClick={() => handleAssistant("risk")}>Why Is My Risk Level?</ActionButton>
        <ActionButton onClick={() => handleAssistant("anomaly")}>Explain Anomaly</ActionButton>
        <ActionButton onClick={() => handleAssistant("summary")}>Summarize For Management</ActionButton>
      </div>
      {assistantLoading && <div style={{ color: "#94a3b8" }}>Thinking...</div>}
      {assistantAnswer && (
        <div style={{ color: "#cbd5e1", lineHeight: 1.7, whiteSpace: "pre-line", background: "#020617", border: "1px solid #1e293b", padding: "16px", borderRadius: "16px" }}>
          {assistantAnswer}
        </div>
      )}
    </SectionCard>
  );
}



function TaniAssistant({ dashboardContext = {} }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      type: "intro",
      text:
        "Hi, I’m Tani AI — your TanSmartX FinOps Copilot. I can help explain spend, forecast risk, governance posture, optimization opportunities, pricing, and platform onboarding.",
    },
    {
      from: "bot",
      type: "insight",
      text:
        "Ask me for an executive summary, current spend explanation, anomaly insight, governance review, optimization plan, or pricing guidance.",
    },
  ]);

  const {
    provider = "GCP",
    displayCurrency = "USD",
    actualBillingSpend = 0,
    budgetStatus = "Healthy",
    budgetUsedPercent = 0,
    governanceScore = 0,
    anomalyLevel = "Low",
    forecastStatus = "Within Budget",
    projectedMonthEndSpend = 0,
    optimizationPotential = 0,
  } = dashboardContext || {};

  const money = (amount, decimals = 2) =>
    `${Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })} ${displayCurrency}`;

  const buildResponse = (rawText) => {
    const q = String(rawText || "").toLowerCase();

    if (q.includes("price") || q.includes("pricing") || q.includes("cost plan") || q.includes("subscription")) {
      return {
        type: "pricing",
        text:
          "TanSmartX can be packaged in three commercial tiers depending on customer maturity and cloud footprint.",
        bullets: [
          "Starter — cloud visibility, basic reporting, and budget tracking for small teams.",
          "Professional — AI insights, anomaly investigation, governance analytics, and executive reports.",
          "Enterprise — multi-cloud control tower, advanced chargeback, custom integrations, SSO, and dedicated support.",
        ],
        next:
          "For a customer conversation, position pricing around monthly cloud spend, number of cloud accounts, and level of managed FinOps support.",
      };
    }

    if (q.includes("contact") || q.includes("demo") || q.includes("call") || q.includes("book")) {
      return {
        type: "action",
        text:
          "For customer engagement, the best next step is a guided platform walkthrough focused on their cloud spend, governance challenges, and optimization goals.",
        bullets: [
          "Collect customer name, company, email, cloud provider, and estimated monthly cloud spend.",
          "Offer a 30-minute FinOps discovery session.",
          "Prepare a sample executive report after the discovery call.",
        ],
        next:
          "Recommended CTA: Book a TanSmartX FinOps Assessment.",
      };
    }

    if (q.includes("summary") || q.includes("cfo") || q.includes("management") || q.includes("executive")) {
      return {
        type: "executive",
        text:
          `Executive Summary: ${String(provider).toUpperCase()} spend is currently ${money(actualBillingSpend)} with budget status marked as ${budgetStatus}. Forecast risk is ${forecastStatus}, and anomaly severity is ${anomalyLevel}.`,
        bullets: [
          `Budget burn: ${Number(budgetUsedPercent || 0).toFixed(1)}% of configured budget.`,
          `Projected month-end spend: ${money(projectedMonthEndSpend)}.`,
          `Governance readiness: ${Number(governanceScore || 0).toFixed(0)}%.`,
          `Estimated optimization potential: ${money(optimizationPotential)}.`,
        ],
        next:
          "Recommended action: review ownership tags, validate top cost drivers, and prioritize optimization actions with cloud operations and finance stakeholders.",
      };
    }

    if (q.includes("spend") || q.includes("billing") || q.includes("current cost") || q.includes("cloud cost")) {
      return {
        type: "spend",
        text:
          `Current Spend Insight: TanSmartX is detecting ${money(actualBillingSpend)} in ${String(provider).toUpperCase()} cloud spend. The current budget posture is ${budgetStatus}.`,
        bullets: [
          `Budget utilization is ${Number(budgetUsedPercent || 0).toFixed(1)}%.`,
          `Month-end forecast is ${forecastStatus}.`,
          `Projected spend is ${money(projectedMonthEndSpend)}.`,
          "Spend should be reviewed together with daily usage trend, top services, and recent deployment activity.",
        ],
        next:
          "Recommended action: compare current burn rate against budget threshold and review the top cost driver table.",
      };
    }

    if (q.includes("anomaly") || q.includes("alert") || q.includes("spike") || q.includes("risk")) {
      return {
        type: "risk",
        text:
          `Anomaly Review: Current anomaly level is ${anomalyLevel}. TanSmartX uses spend trend movement, cost driver changes, and forecast variance to identify financial risk.`,
        bullets: [
          "Check whether the cost increase is caused by new deployments, scaling activity, data transfer, or delayed billing ingestion.",
          "Validate top services and resource ownership.",
          "Create an investigation workflow for high-severity cost movement.",
        ],
        next:
          anomalyLevel === "Low"
            ? "No critical spike is detected now. Continue monitoring trend and budget variance."
            : "Recommended action: open the top cost driver and perform a usage drill-down.",
      };
    }

    if (q.includes("governance") || q.includes("tag") || q.includes("chargeback") || q.includes("owner")) {
      return {
        type: "governance",
        text:
          `Governance Review: Current chargeback readiness is ${Number(governanceScore || 0).toFixed(0)}%. This measures whether resources have useful ownership, environment, business unit, and cost-center metadata.`,
        bullets: [
          "Improve owner and team tags for accountability.",
          "Add environment tags such as production, staging, development, or shared services.",
          "Map resources to cost centers or business units for finance allocation.",
          "Use governance score as a monthly FinOps maturity KPI.",
        ],
        next:
          "Recommended action: start with high-cost untagged resources and assign owners first.",
      };
    }

    if (q.includes("optimize") || q.includes("saving") || q.includes("waste") || q.includes("rightsizing")) {
      return {
        type: "optimization",
        text:
          `Optimization Plan: TanSmartX estimates ${money(optimizationPotential)} in optimization potential from current recommendations and savings workflow.`,
        bullets: [
          "Review idle or underutilized resources.",
          "Check scheduling opportunities for non-production workloads.",
          "Validate rightsizing candidates for compute and database services.",
          "Review storage lifecycle, unattached disks, public IPs, and over-provisioned services.",
        ],
        next:
          "Recommended action: prioritize high-impact, low-effort actions and track them through the optimization pipeline.",
      };
    }

    if (q.includes("aws") || q.includes("azure") || q.includes("gcp") || q.includes("multi")) {
      return {
        type: "cloud",
        text:
          "TanSmartX is designed as a multi-cloud FinOps control tower for AWS, Azure, and Google Cloud cost operations.",
        bullets: [
          "GCP: billing export and BigQuery-based analytics.",
          "AWS: Cost Explorer style spend visibility and optimization roadmap.",
          "Azure: Cost Management and governance roadmap.",
          "Unified view: executive dashboard, governance, anomaly detection, and reporting.",
        ],
        next:
          "For customer pilots, start with one cloud provider and expand to multi-cloud once billing ingestion is stable.",
      };
    }

    return {
      type: "default",
      text:
        "I can help with TanSmartX FinOps platform guidance, cloud cost visibility, AI anomaly investigation, governance, chargeback, optimization workflow, and executive reporting.",
      bullets: [
        "Ask: Explain current spend.",
        "Ask: Generate CFO summary.",
        "Ask: What should I optimize?",
        "Ask: Why is governance score low?",
        "Ask: Explain pricing.",
      ],
      next:
        "Choose one of the suggested prompts below and I’ll provide a more focused FinOps answer.",
    };
  };

  const sendMessage = (customText) => {
    const text = customText || input;
    if (!String(text).trim() || thinking) return;

    const userMessage = { from: "user", text };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setThinking(true);

    setTimeout(() => {
      const response = buildResponse(text);
      setMessages((prev) => [...prev, { from: "bot", ...response }]);
      setThinking(false);
    }, 650);
  };

  const prompts = [
    "Generate CFO summary",
    "Explain current spend",
    "What should I optimize?",
    "Explain governance score",
    "Explain anomaly risk",
    "Pricing guidance",
  ];

  return (
    <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 9999 }}>
      {open && (
        <div
          style={{
            width: "430px",
            maxWidth: "calc(100vw - 28px)",
            height: "680px",
            maxHeight: "calc(100vh - 40px)",
            background: "#ffffff",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 34px 90px rgba(0,0,0,0.42)",
            display: "flex",
            flexDirection: "column",
            border: "1px solid rgba(148,163,184,0.25)",
          }}
        >
          <div
            style={{
              padding: "18px",
              background:
                "radial-gradient(circle at 0% 0%, rgba(125,211,252,0.35), transparent 34%), linear-gradient(135deg,#1D4ED8 0%, #0EA5E9 100%)",
              color: "white",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
              <div>
                <div style={{ fontWeight: 950, fontSize: "22px", letterSpacing: "-0.4px" }}>✨ Tani AI</div>
                <div style={{ fontSize: "13px", opacity: 0.95, marginTop: "4px", fontWeight: 700 }}>
                  Enterprise FinOps Copilot
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  color: "white",
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  fontSize: "22px",
                  cursor: "pointer",
                  lineHeight: "28px",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginTop: "14px" }}>
              <div style={taniHeaderMetric()}>
                <span>Spend</span>
                <strong>{money(actualBillingSpend, 2)}</strong>
              </div>
              <div style={taniHeaderMetric()}>
                <span>Risk</span>
                <strong>{anomalyLevel}</strong>
              </div>
              <div style={taniHeaderMetric()}>
                <span>Gov</span>
                <strong>{Number(governanceScore || 0).toFixed(0)}%</strong>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "16px", background: "#F8FAFC" }}>
            {messages.map((msg, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: msg.from === "user" ? "flex-end" : "flex-start", marginBottom: "12px" }}>
                <div
                  style={{
                    maxWidth: msg.from === "user" ? "82%" : "92%",
                    padding: "13px 15px",
                    borderRadius: msg.from === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    fontSize: "14px",
                    lineHeight: 1.55,
                    background: msg.from === "user" ? "linear-gradient(135deg,#2563EB,#0EA5E9)" : "#ffffff",
                    color: msg.from === "user" ? "#ffffff" : "#0F172A",
                    boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                    border: msg.from === "bot" ? "1px solid #E2E8F0" : "none",
                  }}
                >
                  <div style={{ whiteSpace: "pre-line" }}>{msg.text}</div>

                  {msg.bullets && (
                    <div style={{ marginTop: "10px", display: "grid", gap: "7px" }}>
                      {msg.bullets.map((item, i) => (
                        <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                          <span style={{ color: "#0EA5E9", fontWeight: 950 }}>•</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {msg.next && (
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "10px 12px",
                        borderRadius: "14px",
                        background: "#EFF6FF",
                        color: "#1D4ED8",
                        fontWeight: 800,
                        border: "1px solid #BFDBFE",
                      }}
                    >
                      {msg.next}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {thinking && (
              <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "12px" }}>
                <div style={{ background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: "18px 18px 18px 4px", padding: "13px 15px", color: "#0F172A", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}>
                  <div style={{ fontWeight: 800, color: "#1D4ED8" }}>Analyzing FinOps telemetry...</div>
                  <div style={{ color: "#64748B", fontSize: "13px", marginTop: "4px" }}>Preparing executive-grade response</div>
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: "12px 14px", borderTop: "1px solid #E2E8F0", display: "flex", gap: "8px", flexWrap: "wrap", background: "#ffffff" }}>
            {prompts.map((prompt) => (
              <button key={prompt} onClick={() => sendMessage(prompt)} style={quickBtn()}>
                {prompt}
              </button>
            ))}
          </div>

          <div style={{ padding: "14px", borderTop: "1px solid #E2E8F0", display: "flex", gap: "10px", background: "#ffffff" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Tani about spend, risk, governance..."
              onKeyDown={(e) => {
                if (e.key === "Enter") sendMessage();
              }}
              style={{
                flex: 1,
                border: "1px solid #CBD5E1",
                borderRadius: "16px",
                padding: "12px",
                outline: "none",
                fontSize: "14px",
                color: "#0F172A",
                background: "#FFFFFF",
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={thinking}
              style={{
                border: "none",
                borderRadius: "16px",
                background: thinking ? "#94A3B8" : "linear-gradient(135deg,#2563EB,#0EA5E9)",
                color: "white",
                padding: "0 18px",
                fontWeight: 950,
                cursor: thinking ? "not-allowed" : "pointer",
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            border: "none",
            borderRadius: "999px",
            background: "linear-gradient(135deg,#2563EB,#0EA5E9)",
            color: "white",
            padding: "16px 22px",
            fontWeight: 950,
            fontSize: "15px",
            cursor: "pointer",
            boxShadow: "0 20px 50px rgba(37,99,235,0.45)",
          }}
        >
          ✨ Ask Tani AI
        </button>
      )}
    </div>
  );
}

function taniHeaderMetric() {
  return {
    border: "1px solid rgba(255,255,255,0.22)",
    background: "rgba(255,255,255,0.14)",
    borderRadius: "14px",
    padding: "9px 10px",
    display: "grid",
    gap: "3px",
  };
}


function quickBtn() {
  return {
    border: "1px solid #2563EB",
    background: "#ffffff",
    color: "#2563EB",
    borderRadius: "999px",
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: "12px",
  };
}


function EnterpriseSaasHeader({ plan, trialEndsAt, setPage, signOut }) {
  return (
    <div
      style={{
        maxWidth: "1680px",
        margin: "0 auto",
        padding: "18px 18px 10px",
        position: "sticky",
        top: 0,
        zIndex: 80,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "18px",
          flexWrap: "wrap",
          padding: "14px 18px",
          borderRadius: "28px",
          border: "1px solid rgba(125,211,252,0.18)",
          background: "linear-gradient(135deg, rgba(2,6,23,0.82), rgba(15,23,42,0.72))",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          boxShadow: "0 22px 60px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
      >
        <BrandLockup size={52} titleSize="24px" compact />

        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <button className="tx-button-hover" onClick={() => document.getElementById("tx-command-center")?.scrollIntoView({ behavior: "smooth", block: "start" })} style={saasNavLink(true)}>Platform</button>
          <button className="tx-button-hover" onClick={() => setPage("pricing")} style={saasNavLink(false)}>Pricing</button>
          <button className="tx-button-hover" onClick={() => document.getElementById("tx-ai-section")?.scrollIntoView({ behavior: "smooth", block: "start" })} style={saasNavLink(false)}>AI FinOps</button>
          <div style={planBadgeStyle(plan)}>
            {PLAN_LABEL[plan] || "Basic"} Plan{trialEndsAt ? " · Trial until " + trialEndsAt.toLocaleDateString() : ""}
          </div>
          <button className="tx-button-hover" onClick={() => setPage("pricing")} style={saasPrimaryNavButton()}>Upgrade</button>
          <button
            onClick={() => {
              localStorage.removeItem("mfa_verified");
              localStorage.removeItem("mfa_verified_uid");
              signOut(auth);
            }}
            style={saasNavLink(false)}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function EnterpriseProductVisual({ displayCurrency, actualBillingSpend, budgetStatus, budgetForecast, provider }) {
  const previewData = [
    { label: "Mon", cost: 12 },
    { label: "Tue", cost: 18 },
    { label: "Wed", cost: 16 },
    { label: "Thu", cost: 26 },
    { label: "Fri", cost: 21 },
    { label: "Now", cost: Math.max(28, Number(actualBillingSpend || 0) || 32) },
  ];

  return (
    <div
      style={{
        position: "relative",
        minHeight: "420px",
        borderRadius: "32px",
        border: "1px solid rgba(125,211,252,0.25)",
        background: "radial-gradient(circle at 20% 0%, rgba(34,211,238,0.22), transparent 30%), linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))",
        boxShadow: "0 28px 90px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.07)",
        overflow: "hidden",
        padding: "22px",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, transparent 0%, rgba(37,99,235,0.12) 100%)" }} />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", marginBottom: "18px" }}>
          <div>
            <div style={{ color: "white", fontWeight: 950, fontSize: "18px" }}>Live FinOps Command View</div>
            <div style={{ color: "#94a3b8", fontSize: "12px", marginTop: "4px" }}>AWS · Azure · GCP · AI Governance</div>
          </div>
          <Badge color={budgetStatus === "Exceeded" ? COLORS.red : budgetStatus === "Warning" ? COLORS.amber : COLORS.green}>{budgetStatus}</Badge>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "16px" }}>
          <MiniGlassMetric title="Monthly Spend" value={formatMoney(displayCurrency, actualBillingSpend, 2)} />
          <MiniGlassMetric title="Forecast" value={budgetForecast.forecastStatus} />
          <MiniGlassMetric title="Provider" value={provider.toUpperCase()} />
        </div>

        <div style={{ height: "170px", borderRadius: "22px", border: "1px solid rgba(148,163,184,0.18)", background: "rgba(2,6,23,0.62)", padding: "14px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={previewData}>
              <defs>
                <linearGradient id="enterprisePreviewGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.42} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(148,163,184,0.12)" strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle()} />
              <Area type="monotone" dataKey="cost" stroke="#22D3EE" fill="url(#enterprisePreviewGradient)" strokeWidth={3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: "14px", marginTop: "16px" }}>
          <div style={visualPanelStyle()}>
            <div style={{ color: "#93C5FD", fontWeight: 900, fontSize: "12px", marginBottom: "8px" }}>AI INSIGHT</div>
            <div style={{ color: "white", fontWeight: 900, lineHeight: 1.35 }}>Potential waste detected in untagged resources.</div>
            <div style={{ color: "#94a3b8", fontSize: "12px", marginTop: "8px" }}>Recommended: validate owner, environment and cost center tags.</div>
          </div>
          <div style={visualPanelStyle()}>
            <div style={{ color: "#67E8F9", fontWeight: 900, fontSize: "12px", marginBottom: "10px" }}>CLOUDS</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {["AWS", "Azure", "GCP"].map((item) => <span key={item} style={cloudPill()}>{item}</span>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniGlassMetric({ title, value }) {
  return (
    <div style={{ border: "1px solid rgba(148,163,184,0.18)", background: "rgba(15,23,42,0.72)", borderRadius: "18px", padding: "14px" }}>
      <div style={{ color: "#94a3b8", fontSize: "11px", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.7px" }}>{title}</div>
      <div style={{ color: "white", fontSize: "16px", fontWeight: 950, marginTop: "8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
    </div>
  );
}

function EnterpriseSaasHero({
  plan,
  trialEndsAt,
  setPage,
  signOut,
  provider,
  setProvider,
  displayCurrency,
  setDisplayCurrency,
  granularity,
  setGranularity,
  isAws,
  isAzure,
  exportingPdf,
  dashboardData,
  handleExportPdf,
  aiLoading,
  isStandardOrHigher,
  handleGenerateAiSummary,
  setUpgradeModal,
  activeTab,
  setActiveTab,
  fxUpdatedAt,
  actualBillingSpend,
  budgetStatus,
  budgetForecast,
}) {
  return (
    <>
      <EnterpriseSaasHeader plan={plan} trialEndsAt={trialEndsAt} setPage={setPage} signOut={signOut} />
      <div style={{ maxWidth: "1680px", margin: "0 auto", padding: "28px 18px 34px" }}>
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "42px",
            border: "1px solid rgba(125,211,252,0.22)",
            background: "radial-gradient(circle at 8% 0%, rgba(34,211,238,0.20), transparent 28%), radial-gradient(circle at 78% 18%, rgba(37,99,235,0.20), transparent 28%), linear-gradient(135deg, rgba(2,6,23,0.98), rgba(8,17,38,0.96))",
            boxShadow: "0 36px 120px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.06)",
            padding: "44px",
          }}
        >
          <div style={{ position: "absolute", right: "-120px", top: "-120px", width: "380px", height: "380px", borderRadius: "50%", background: "rgba(34,211,238,0.14)", filter: "blur(20px)" }} />
          <div style={{ position: "absolute", left: "-100px", bottom: "-130px", width: "360px", height: "360px", borderRadius: "50%", background: "rgba(37,99,235,0.16)", filter: "blur(22px)" }} />

          <div style={{ position: "relative", zIndex: 2, display: "grid", gridTemplateColumns: "minmax(320px, 0.95fr) minmax(360px, 1.05fr)", gap: "38px", alignItems: "center" }}>
            <div>
              <div style={{ marginBottom: "22px" }}>
                <BrandLockup size={78} titleSize="38px" />
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
                <Badge color={COLORS.blue}>Enterprise FinOps Platform</Badge>
                <Badge color={COLORS.cyan}>AI Cost Intelligence</Badge>
                <Badge color={COLORS.green}>MFA Secured</Badge>
              </div>

              <h1 style={{ margin: 0, maxWidth: "760px", fontSize: "clamp(46px, 5vw, 82px)", lineHeight: 0.92, letterSpacing: "-3.2px", fontWeight: 950 }}>
                Control cloud spend. Govern at scale.
              </h1>
              <p style={{ color: "#BAE6FD", margin: "22px 0 0", maxWidth: "760px", fontSize: "20px", lineHeight: 1.65, fontWeight: 650 }}>
                TanSmartX unifies AWS, Azure and GCP cost visibility, anomaly investigation, executive reporting and chargeback readiness in one AI-powered FinOps control tower.
              </p>

              <div style={{ color: "#64748b", fontSize: "13px", marginTop: "14px", fontWeight: 800 }}>
                {fxUpdatedAt} · Display currency: {CURRENCY_META[displayCurrency]?.name || displayCurrency} · Current plan: {PLAN_LABEL[plan] || "Basic"}
              </div>

              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "26px" }}>
                <ActionButton
                  onClick={() => {
                    if (plan === "basic") {
                      setUpgradeModal({
                        title: "Unlock Executive Reports",
                        eyebrow: "Standard & Enterprise Feature",
                        description: "Generate branded executive PDF reports with financial overview, risk, anomaly insights, and recommended actions.",
                        benefits: ["Professional executive PDF reporting", "Client-ready governance and budget summaries", "Downloadable reports for leadership reviews"],
                      });
                      return;
                    }
                    handleExportPdf();
                  }}
                  disabled={exportingPdf || !dashboardData}
                  variant="primary"
                >
                  {exportingPdf ? "Generating..." : plan === "basic" ? "Unlock Executive Reports" : "Export Executive PDF"}
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    if (!isStandardOrHigher) {
                      setUpgradeModal({
                        title: "Unlock AI Insights",
                        eyebrow: "Standard & Enterprise Feature",
                        description: "Use AI-powered FinOps analysis to explain spend, identify risks, and recommend optimization actions.",
                        benefits: ["AI executive summaries", "Anomaly explanation and business impact", "Optimization recommendations for cloud teams"],
                      });
                      return;
                    }
                    handleGenerateAiSummary();
                  }}
                  disabled={aiLoading || !dashboardData}
                >
                  {aiLoading ? "Generating AI..." : isStandardOrHigher ? "Generate AI Summary" : "Unlock AI Insights"}
                </ActionButton>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px", marginTop: "28px", maxWidth: "720px" }}>
                {["Cost Optimization", "Anomaly Detection", "Chargeback", "TBM View"].map((item) => (
                  <div key={item} style={{ border: "1px solid rgba(148,163,184,0.18)", borderRadius: "16px", padding: "12px 14px", background: "rgba(15,23,42,0.58)", color: "#E0F2FE", fontWeight: 850, fontSize: "13px" }}>
                    ✓ {item}
                  </div>
                ))}
              </div>
            </div>

            <EnterpriseProductVisual displayCurrency={displayCurrency} actualBillingSpend={actualBillingSpend} budgetStatus={budgetStatus} budgetForecast={budgetForecast} provider={provider} />
          </div>

          <div style={{ position: "relative", zIndex: 2, marginTop: "34px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <select value={provider} onChange={(e) => setProvider(e.target.value)} style={inputStyle()}>
                <option value="multi" disabled={plan === "basic"}>Multi-cloud {plan === "basic" ? "(Upgrade required)" : ""}</option>
                <option value="gcp">GCP</option>
                <option value="aws">AWS</option>
                <option value="azure">Azure Preview</option>
              </select>
              <select value={displayCurrency} onChange={(e) => setDisplayCurrency(e.target.value)} style={inputStyle()}>
                <option value="USD">USD</option>
                <option value="MYR">MYR</option>
                <option value="SGD">SGD</option>
                <option value="AUD">AUD</option>
              </select>
              <select value={granularity} onChange={(e) => setGranularity(e.target.value)} disabled={isAws || isAzure} style={inputStyle(isAws || isAzure)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          <div style={{ position: "relative", zIndex: 2, marginTop: "22px" }}>
            <ApptioTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>
        </div>
      </div>
    </>
  );
}



function buildEnterpriseRealtimeTrend(combinedTrend = [], actualBillingSpend = 0, budgetForecast = {}) {
  const hasUsefulDailyData = Array.isArray(combinedTrend) && combinedTrend.length >= 5 && combinedTrend.some((item) => {
    const label = String(item.label || "");
    return label.length >= 8 && label.includes("-");
  });

  if (hasUsefulDailyData) {
    return combinedTrend.map((item) => ({
      ...item,
      label: String(item.label || "").slice(5) || item.label,
    }));
  }

  const now = new Date();
  const days = 14;
  const currentSpend = Math.max(Number(actualBillingSpend || 0), 0.01);
  const projected = Math.max(Number(budgetForecast?.projectedMonthEndSpend || currentSpend * 1.25), currentSpend);

  return Array.from({ length: days }, (_, index) => {
    const d = new Date(now);
    d.setDate(now.getDate() - (days - 1 - index));
    const label = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
    const progress = index / (days - 1);
    const wave = Math.sin(index * 1.25) * currentSpend * 0.025;
    const actualCost = index < days - 2
      ? Math.max(currentSpend * (0.52 + progress * 0.43) + wave, 0)
      : currentSpend;

    const forecastCost = index >= days - 4
      ? currentSpend + ((projected - currentSpend) * ((index - (days - 4)) / 3))
      : null;

    return {
      label,
      actualCost: Number(actualCost.toFixed(4)),
      forecastCost: forecastCost === null ? null : Number(forecastCost.toFixed(4)),
    };
  });
}

function EnterpriseRealtimeCommandCenter({
  provider,
  displayCurrency,
  actualBillingSpend,
  monthlyBudget,
  budgetUsedPercent,
  budgetStatus,
  budgetColor,
  budgetForecast,
  forecastColor,
  anomaly,
  combinedTrend,
  cloudDistribution,
  comparisonData,
  governance,
  savingsOpportunities,
  fxRates,
  granularity,
  topCloud,
}) {
  const liveTrendData = buildEnterpriseRealtimeTrend(combinedTrend, actualBillingSpend, budgetForecast);
  const liveUpdatedAt = new Date().toLocaleString("en-MY", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const optimizationPotential = savingsOpportunities.reduce((sum, item) => sum + Number(item.estimatedSavings || 0), 0);

  return (
    <SectionCard
      title="Real-Time Executive Command Center"
      badge={<Badge color={budgetColor}>Live FinOps Signal · {provider.toUpperCase()}</Badge>}
      right={<div style={{ color: "#94a3b8", fontWeight: 800, fontSize: "13px" }}>{granularity.toUpperCase()} telemetry · AI risk view</div>}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", marginBottom: "20px" }}>
        <CommandMetric title="Live Cloud Spend" value={formatMoney(displayCurrency, actualBillingSpend, 2)} subtext="Current detected billing spend" color={COLORS.blue} />
        <CommandMetric title="Budget Burn" value={`${budgetUsedPercent.toFixed(1)}%`} subtext={`${budgetStatus} against monthly budget`} color={budgetColor} />
        <CommandMetric title="Forecast Risk" value={budgetForecast.forecastStatus} subtext={`Month-end ${formatMoney(displayCurrency, budgetForecast.projectedMonthEndSpend, 2)}`} color={forecastColor} />
        <CommandMetric title="Optimization Potential" value={formatMoney(displayCurrency, convertAmount(optimizationPotential, "USD", displayCurrency, fxRates), 2)} subtext="Estimated savings pipeline" color={COLORS.green} />
        <CommandMetric title="Governance Score" value={`${governance.score.toFixed(0)}%`} subtext="Chargeback readiness" color={governance.score >= 70 ? COLORS.green : COLORS.amber} />
        <CommandMetric title="Anomaly Level" value={anomaly.level} subtext={anomaly.status} color={anomaly.level === "High" ? COLORS.red : anomaly.level === "Medium" ? COLORS.amber : COLORS.green} pulse={anomaly.level !== "Low"} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 1.55fr) minmax(280px, 0.9fr)", gap: "18px" }}>
        <div style={premiumAnalyticsCard()}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", marginBottom: "14px", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "white", fontWeight: 950, fontSize: "20px" }}>Live Spend Trend</div>
              <div style={{ color: "#94a3b8", fontSize: "13px", marginTop: "4px" }}>Rolling 14-day live view · synced {liveUpdatedAt}</div>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={livePulseStyle()} />
              <Badge color={COLORS.cyan}>Enterprise Real-Time</Badge>
            </div>
          </div>
          <div style={{ height: "360px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={liveTrendData}>
                <defs>
                  <linearGradient id="liveActualGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.42} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="liveForecastGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.30} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(148,163,184,0.15)" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 12 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle()} />
                <Legend />
                <Area type="monotone" dataKey="actualCost" name="Actual Cost" stroke="#38BDF8" fill="url(#liveActualGradient)" strokeWidth={3} connectNulls />
                <Area type="monotone" dataKey="forecastCost" name="Forecast Cost" stroke="#F59E0B" fill="url(#liveForecastGradient)" strokeWidth={3} strokeDasharray="6 4" connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ display: "grid", gap: "18px" }}>
          <div style={premiumAnalyticsCard()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ color: "white", fontWeight: 950, fontSize: "18px" }}>Cloud Distribution</div>
              <Badge color={COLORS.green}>{topCloud?.cloud || provider.toUpperCase()}</Badge>
            </div>
            <div style={{ height: "220px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={cloudDistribution} dataKey="value" nameKey="name" innerRadius={55} outerRadius={82} paddingAngle={4}>
                    {cloudDistribution.map((entry, index) => <Cell key={`command-cloud-${index}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle()} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={premiumAnalyticsCard()}>
            <div style={{ color: "white", fontWeight: 950, fontSize: "18px", marginBottom: "12px" }}>Budget Variance</div>
            <div style={{ height: "210px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData}>
                  <CartesianGrid stroke="rgba(148,163,184,0.15)" strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip contentStyle={tooltipStyle()} />
                  <Legend />
                  <Bar dataKey="actual" name="Actual" fill={COLORS.blue} radius={[10, 10, 0, 0]} />
                  <Bar dataKey="forecast" name="Forecast" fill={COLORS.amber} radius={[10, 10, 0, 0]} />
                  <Bar dataKey="budget" name="Budget" fill={COLORS.purple} radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function EnterpriseProductStorytelling({ anomaly, aiAnomaly, governance, savingsOpportunities, displayCurrency, fxRates }) {
  const cards = [
    {
      title: "AI Anomaly Investigation",
      label: anomaly.status,
      color: anomaly.level === "High" ? COLORS.red : anomaly.level === "Medium" ? COLORS.amber : COLORS.green,
      text: aiAnomaly.cause,
    },
    {
      title: "Chargeback Readiness",
      label: `${governance.score.toFixed(0)}%`,
      color: governance.score >= 70 ? COLORS.green : COLORS.amber,
      text: "Owner, team, environment and cost-center tagging quality for finance allocation.",
    },
    {
      title: "Optimization Workflow",
      label: `${savingsOpportunities.length} actions`,
      color: COLORS.blue,
      text: "Prioritized rightsizing, scheduling, lifecycle, reserved capacity and unused asset recommendations.",
    },
  ];

  return (
    <SectionCard title="Enterprise Product Storyboard" badge={<Badge color={COLORS.purple}>CFO · CIO · Cloud Ops View</Badge>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px" }}>
        {cards.map((card) => (
          <div key={card.title} style={{ border: `1px solid ${card.color}44`, borderRadius: "24px", padding: "20px", background: `radial-gradient(circle at 100% 0%, ${card.color}22, transparent 35%), linear-gradient(180deg, rgba(15,23,42,0.92), rgba(2,6,23,0.78))`, minHeight: "180px" }}>
            <Badge color={card.color}>{card.label}</Badge>
            <div style={{ color: "white", fontSize: "20px", fontWeight: 950, marginTop: "16px" }}>{card.title}</div>
            <div style={{ color: "#cbd5e1", lineHeight: 1.65, marginTop: "10px" }}>{card.text}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", marginTop: "18px" }}>
        {savingsOpportunities.slice(0, 4).map((item) => (
          <div key={item.id} style={{ border: "1px solid rgba(148,163,184,0.18)", borderRadius: "18px", background: "rgba(2,6,23,0.55)", padding: "16px" }}>
            <div style={{ color: "#93C5FD", fontWeight: 950, fontSize: "13px" }}>{item.category}</div>
            <div style={{ color: "white", fontWeight: 900, marginTop: "8px" }}>{item.title}</div>
            <div style={{ color: COLORS.green, fontWeight: 950, marginTop: "10px" }}>{formatMoney(displayCurrency, convertAmount(item.estimatedSavings, "USD", displayCurrency, fxRates), 2)} potential</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function CommandMetric({ title, value, subtext, color, pulse = false }) {
  return (
    <div className="tx-card-hover" style={{ position: "relative", overflow: "hidden", border: `1px solid ${color}44`, borderRadius: "22px", padding: "18px", background: `radial-gradient(circle at 100% 0%, ${color}22, transparent 38%), rgba(2,6,23,0.72)`, boxShadow: pulse ? `0 0 0 1px ${color}33, 0 0 34px ${color}22` : "none" }}>
      {pulse && <div style={{ position: "absolute", right: 14, top: 14, width: 10, height: 10, borderRadius: "50%", background: color, boxShadow: `0 0 0 8px ${color}22` }} />}
      <div style={{ color: "#94a3b8", fontSize: "12px", fontWeight: 950, textTransform: "uppercase", letterSpacing: "1px" }}>{title}</div>
      <div style={{ color: "white", fontSize: "24px", fontWeight: 950, marginTop: "10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      <div style={{ color: "#cbd5e1", fontSize: "13px", marginTop: "8px", lineHeight: 1.45 }}>{subtext}</div>
    </div>
  );
}


function livePulseStyle() {
  return {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
    background: "#22C55E",
    boxShadow: "0 0 0 6px rgba(34,197,94,0.14), 0 0 22px rgba(34,197,94,0.72)",
    display: "inline-block",
  };
}

function premiumAnalyticsCard() {
  return {
    border: "1px solid rgba(125,211,252,0.18)",
    borderRadius: "26px",
    background: "radial-gradient(circle at 0% 0%, rgba(14,165,233,0.12), transparent 30%), linear-gradient(180deg, rgba(15,23,42,0.88), rgba(2,6,23,0.82))",
    padding: "20px",
    boxShadow: "0 18px 50px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.04)",
  };
}

function EnterprisePlatformShowcase({ onPreview }) {
  const layers = [
    { title: "AI FinOps Layer", text: "Executive summaries, anomaly explanation and optimization guidance." },
    { title: "Governance & Chargeback", text: "Owner, environment, business unit and cost-center readiness." },
    { title: "Optimization Engine", text: "Rightsizing, unused assets, budget alerts and savings workflow." },
    { title: "Cloud Billing Sources", text: "AWS Cost Explorer, Azure Cost Management and GCP Billing Export." },
  ];

  return (
    <SectionCard title="Enterprise Platform Architecture" badge={<Badge color={COLORS.cyan}>SaaS Control Plane</Badge>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "14px" }}>
        {layers.map((layer, index) => (
          <div
            key={layer.title}
            className="tx-card-hover tx-clickable"
            onClick={() => onPreview?.({
              eyebrow: "Platform Layer",
              title: layer.title,
              description: layer.text,
              benefits: ["Designed as an enterprise SaaS capability", "Can be connected to real cloud data sources", "Supports customer-demo and production roadmap positioning"],
              ctaLabel: "View Pricing",
              secondaryLabel: "Close",
            })}
            style={{ border: "1px solid rgba(125,211,252,0.18)", borderRadius: "20px", padding: "18px", background: "linear-gradient(180deg, rgba(15,23,42,0.82), rgba(2,6,23,0.70))" }}
          >
            <div style={{ width: "34px", height: "34px", borderRadius: "12px", background: "linear-gradient(135deg,#2563EB,#22D3EE)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 950, marginBottom: "14px" }}>{index + 1}</div>
            <div style={{ color: "white", fontWeight: 950, fontSize: "17px" }}>{layer.title}</div>
            <div style={{ color: "#94a3b8", fontSize: "14px", lineHeight: 1.6, marginTop: "8px" }}>{layer.text}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "18px" }}>
        {["AWS", "Azure", "Google Cloud", "Kubernetes", "Terraform", "Datadog", "Jira", "ServiceNow"].map((item) => (
          <span key={item} style={integrationPill()}>{item}</span>
        ))}
      </div>
    </SectionCard>
  );
}

function visualPanelStyle() {
  return {
    border: "1px solid rgba(148,163,184,0.18)",
    background: "rgba(15,23,42,0.72)",
    borderRadius: "18px",
    padding: "14px",
  };
}

function cloudPill() {
  return {
    border: "1px solid rgba(125,211,252,0.30)",
    background: "rgba(14,165,233,0.12)",
    color: "#E0F2FE",
    padding: "7px 10px",
    borderRadius: "999px",
    fontWeight: 900,
    fontSize: "12px",
  };
}

function integrationPill() {
  return {
    border: "1px solid rgba(125,211,252,0.20)",
    background: "rgba(14,165,233,0.08)",
    color: "#BAE6FD",
    padding: "10px 13px",
    borderRadius: "999px",
    fontWeight: 850,
    fontSize: "13px",
  };
}

function saasNavLink(active = false) {
  return {
    border: "1px solid rgba(148,163,184,0.16)",
    borderRadius: "999px",
    background: active ? "rgba(37,99,235,0.22)" : "rgba(2,6,23,0.18)",
    color: active ? "#DBEAFE" : "#CBD5E1",
    padding: "11px 15px",
    fontWeight: 900,
    cursor: "pointer",
  };
}

function saasPrimaryNavButton() {
  return {
    border: "none",
    borderRadius: "999px",
    background: "linear-gradient(135deg,#2563EB,#22D3EE)",
    color: "white",
    padding: "12px 18px",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 32px rgba(37,99,235,0.34)",
  };
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [checkingMfa, setCheckingMfa] = useState(true);
  const [userMfaEnabled, setUserMfaEnabled] = useState(false);
  const [mfaSessionVerified, setMfaSessionVerified] = useState(false);
  const [page, setPage] = useState("dashboard");
  const [plan, setPlan] = useState("basic");
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(null);

  useEffect(() => {
    const savedMfa = localStorage.getItem("mfa_verified") === "true";
    if (savedMfa) {
      setMfaSessionVerified(true);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("🔥 Auth State User:", currentUser?.email || "No user");
      setUser(currentUser);
      const savedMfa = localStorage.getItem("mfa_verified") === "true";
      const savedUid = localStorage.getItem("mfa_verified_uid");
      setMfaSessionVerified(Boolean(currentUser && savedMfa && savedUid === currentUser.uid));
      setCheckingMfa(true);

      if (currentUser) {
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDocFromServer(userRef);
          const userData = userSnap.exists() ? userSnap.data() : {};
          const enabled = userData.mfaEnabled === true;
          console.log("🔥 MFA Enabled:", enabled);
          setUserMfaEnabled(enabled);

          const savedPlan = userData.plan || "basic";
          setPlan(["basic", "standard", "enterprise"].includes(savedPlan) ? savedPlan : "basic");
          setTrialEndsAt(userData.trialEndsAt?.toDate?.() || null);
        } catch (err) {
          console.error("MFA / plan check failed:", err);
          setUserMfaEnabled(false);
          setPlan("basic");
          setTrialEndsAt(null);
        }
      } else {
        setUserMfaEnabled(false);
        setPlan("basic");
        setTrialEndsAt(null);
      }

      setCheckingMfa(false);
      setLoadingAuth(false);
    });

    return () => unsubscribe();
  }, []);
  const [dashboardData, setDashboardData] = useState(null);
  const [multiCloudData, setMultiCloudData] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [assistantAnswer, setAssistantAnswer] = useState("");
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState("");
  const [granularity, setGranularity] = useState("daily");
  const [provider, setProvider] = useState("multi");
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [monthlyBudget, setMonthlyBudget] = useState(50);
  const [backendBudgetAlert, setBackendBudgetAlert] = useState(null);
  const [fxRates, setFxRates] = useState(DEFAULT_FX_RATES);
  const [fxUpdatedAt, setFxUpdatedAt] = useState("Static demo rates");
  const [activeTab, setActiveTab] = useState("executive");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [optimizationStatus, setOptimizationStatus] = useState({});
  const [qrCode, setQrCode] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSetup, setMfaSetup] = useState(false);
  const [tbmData, setTbmData] = useState(null);
  const [tbmBusinessUnits, setTbmBusinessUnits] = useState([]);
  const [tbmApplications, setTbmApplications] = useState([]);
  const [tbmUnmapped, setTbmUnmapped] = useState(null);
  const [tbmLoading, setTbmLoading] = useState(false);
  const [tbmError, setTbmError] = useState("");

  const isStandardOrHigher = isPlanAtLeast(plan, "standard");

  useEffect(() => {
    if (plan === "basic" && provider === "multi") {
      setProvider("gcp");
    }
  }, [plan, provider]);

  const handleSelectPlan = async (selectedPlan) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert("Please sign in again before selecting a plan.");
        return;
      }

      setPlanSaving(true);
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 14);

      await setDoc(
        doc(db, "users", currentUser.uid),
        {
          plan: selectedPlan,
          trial: true,
          trialStartedAt: serverTimestamp(),
          trialEndsAt: Timestamp.fromDate(trialEndDate),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setPlan(selectedPlan);
      setTrialEndsAt(trialEndDate);
      setPage("dashboard");
      alert((PLAN_LABEL[selectedPlan] || selectedPlan) + " plan activated for this account.");
    } catch (err) {
      console.error("Plan update failed:", err);
      alert(err?.message || "Unable to update plan. Please check Firestore permissions.");
    } finally {
      setPlanSaving(false);
    }
  };

  const handleEnableMfa = async () => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        alert("User not logged in. Please login again before enabling MFA.");
        return;
      }

      const idToken = await currentUser.getIdToken(true);
      const response = await fetch("https://us-central1-my-firstproject-350605-a54a7.cloudfunctions.net/generateMfaSecret", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      const result = await response.json();

      if (!result.success || !result.qrCode) {
        alert(result.error || "Unable to generate MFA QR code.");
        return;
      }

      setQrCode(result.qrCode);
      setMfaSetup(true);
    } catch (err) {
      console.error("MFA setup failed:", err);
      alert(err?.message || "Unable to generate MFA QR code.");
    }
  };

  const handleVerifyMfa = async () => {
    try {
      if (!mfaCode || mfaCode.trim().length !== 6) {
        alert("Please enter the 6-digit code from Google Authenticator.");
        return;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert("User not logged in. Please login again before verifying MFA.");
        return;
      }

      const idToken = await currentUser.getIdToken(true);
      const response = await fetch("https://us-central1-my-firstproject-350605-a54a7.cloudfunctions.net/verifyMfaCode", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ token: mfaCode.trim() }),
      });

      const result = await response.json();

      if (!result.success) {
        alert(result.error || "Invalid MFA code. Please try again.");
        return;
      }

      alert("MFA Enabled Successfully!");
      setMfaSetup(false);
      setQrCode(null);
      setMfaCode("");
      setUserMfaEnabled(true);
      localStorage.setItem("mfa_verified", "true");
      localStorage.setItem("mfa_verified_uid", currentUser.uid);
      setMfaSessionVerified(true);
    } catch (err) {
      console.error("MFA verification failed:", err);
      alert(err?.message || "Invalid MFA code. Please try again.");
    }
  };

  useEffect(() => {
    async function loadFxRates() {
      try {
        const response = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
        if (!response.ok) throw new Error("FX API unavailable");
        const data = await response.json();

        setFxRates({
          USD: 1,
          MYR: data.rates?.MYR || DEFAULT_FX_RATES.MYR,
          SGD: data.rates?.SGD || DEFAULT_FX_RATES.SGD,
          AUD: data.rates?.AUD || DEFAULT_FX_RATES.AUD,
        });
        setFxUpdatedAt(data.date ? `Live FX updated: ${data.date}` : `Live FX updated: ${new Date().toLocaleDateString()}`);
      } catch (err) {
        console.warn("FX API failed. Using static fallback rates.", err);
        setFxRates(DEFAULT_FX_RATES);
        setFxUpdatedAt("Static fallback FX rates");
      }
    }

    loadFxRates();
  }, []);

  useEffect(() => {
    async function loadTbmData() {
      const fetchJsonSafe = async (url, fallback) => {
        try {
          const response = await fetch(url, {
            method: "GET",
            headers: { Accept: "application/json" },
          });

          if (!response.ok) {
            console.warn("TBM endpoint returned non-OK:", url, response.status);
            return fallback;
          }

          return await response.json();
        } catch (err) {
          console.warn("TBM endpoint failed:", url, err);
          return fallback;
        }
      };

      try {
        setTbmLoading(true);
        setTbmError("");

        const summary = await fetchJsonSafe(`${TBM_API_BASE}/api/tbm/summary`, null);

        if (!summary) {
          setTbmData(null);
          setTbmBusinessUnits([]);
          setTbmApplications([]);
          setTbmUnmapped({ data: [] });
          setTbmError("TBM summary API is not reachable. Open the Test Summary API button below to verify Cloud Run.");
          return;
        }

        const [bu, apps, unmapped] = await Promise.all([
          fetchJsonSafe(`${TBM_API_BASE}/api/tbm/cost-by-business-unit`, { data: [] }),
          fetchJsonSafe(`${TBM_API_BASE}/api/tbm/cost-by-application`, { data: [] }),
          fetchJsonSafe(`${TBM_API_BASE}/api/tbm/unmapped-costs`, { data: [] }),
        ]);

        setTbmData(summary);
        setTbmBusinessUnits(Array.isArray(bu?.data) ? bu.data : []);
        setTbmApplications(Array.isArray(apps?.data) ? apps.data : []);
        setTbmUnmapped(unmapped || { data: [] });
      } catch (err) {
        console.error("TBM error:", err);
        setTbmError(err?.message || "Failed to load TBM data");
      } finally {
        setTbmLoading(false);
      }
    }

    loadTbmData();
  }, []);

  useEffect(() => {
    async function loadAll() {
      try {
        setError("");
        setDashboardData(null);
        setAiSummary(null);
        setAssistantAnswer("");
        setMultiCloudData([]);

        if (provider === "multi") {
          const [gcpDashboard, awsResponse] = await Promise.allSettled([
            fetchDashboard(projectId, granularity),
            fetch(`${API_BASE}/api/aws/dashboard`).then((res) => res.json()),
          ]);

          const gcpData = gcpDashboard.status === "fulfilled" ? gcpDashboard.value : null;
          const awsData = awsResponse.status === "fulfilled" ? awsResponse.value : null;
          const gcpSpend = Number(gcpData?.billing?.totalMonthlySpend || 0);
          const awsSpend = Number(awsData?.totalMonthlySpend || 0);
          const azureSpend = 0;

          const cloudBreakdown = [
            { cloud: "GCP", spend: gcpSpend, status: gcpData ? "Connected" : "Unavailable", color: cloudColors.GCP },
            { cloud: "AWS", spend: awsSpend, status: awsData?.message || "Connected", color: cloudColors.AWS },
            { cloud: "Azure", spend: azureSpend, status: "Preview", color: cloudColors.Azure },
          ];

          setMultiCloudData(cloudBreakdown);

          const totalSpend = gcpSpend + awsSpend + azureSpend;
          const topCloud = cloudBreakdown.reduce((max, item) => (item.spend > max.spend ? item : max), cloudBreakdown[0]);

          setDashboardData({
            provider: "multi",
            totalMonthlySpend: totalSpend,
            currency: "USD",
            trend: gcpData?.trend || [],
            costDrivers: cloudBreakdown.map((item) => ({ name: item.cloud, service: item.cloud, cost: item.spend })),
            riskLevel: totalSpend > monthlyBudget ? "High" : "Low",
            message: `Multi-cloud connected. Top cloud by spend: ${topCloud.cloud}.`,
            resources: gcpData?.resources || [],
            summary: gcpData?.summary || {},
            billing: { totalMonthlySpend: totalSpend, currency: "USD" },
          });
          return;
        }

        if (provider === "aws") {
          const response = await fetch(`${API_BASE}/api/aws/dashboard`);
          if (!response.ok) throw new Error("Failed to load AWS dashboard");
          setDashboardData(await response.json());
          return;
        }

        if (provider === "azure") {
          setDashboardData({
            provider: "azure",
            totalMonthlySpend: 0,
            currency: "USD",
            trend: [],
            costDrivers: [],
            riskLevel: "Low",
            message: "Azure integration is not connected yet. This is a roadmap preview.",
            resources: [],
            summary: {},
            billing: { totalMonthlySpend: 0, currency: "USD" },
          });
          return;
        }

        const dashboard = await fetchDashboard(projectId, granularity);
        setDashboardData(dashboard);
      } catch (err) {
        console.error("Load Error:", err);
        setError(err.message || "Failed to load dashboard");
      }
    }

    loadAll();
  }, [granularity, provider, monthlyBudget]);

  const isAws = provider === "aws";
  const isAzure = provider === "azure";
  const isMulti = provider === "multi";
  const summary = dashboardData?.summary || {};
  const resources = dashboardData?.resources || [];
  const trend = dashboardData?.trend || [];
  const forecast = dashboardData?.forecast || {};
  const costDrivers = dashboardData?.costDrivers || [];
  const anomaly = detectAnomaly(trend);

  const actualBillingSpendRaw = isAws || isAzure || isMulti ? Number(dashboardData?.totalMonthlySpend || 0) : Number(dashboardData?.billing?.totalMonthlySpend || 0);
  const estimatedResourceCostRaw = Number(summary?.estimatedMonthlySpend || 0);
  const actualBillingSpend = convertAmount(actualBillingSpendRaw, "USD", displayCurrency, fxRates);
  const estimatedResourceCost = convertAmount(estimatedResourceCostRaw, "USD", displayCurrency, fxRates);

  const budgetUsedPercent = monthlyBudget > 0 ? (actualBillingSpend / monthlyBudget) * 100 : 0;
  const budgetStatus = budgetUsedPercent >= 100 ? "Exceeded" : budgetUsedPercent >= 80 ? "Warning" : "Healthy";
  const budgetColor = budgetStatus === "Exceeded" ? COLORS.red : budgetStatus === "Warning" ? COLORS.amber : COLORS.green;
  const budgetForecast = buildBudgetForecast(actualBillingSpend, monthlyBudget);
  const forecastColor = budgetForecast.forecastStatus === "Will Exceed" ? COLORS.red : COLORS.green;

  const topCloud = useMemo(() => {
    if (!multiCloudData.length) return null;
    return multiCloudData.reduce((max, item) => (item.spend > max.spend ? item : max), multiCloudData[0]);
  }, [multiCloudData]);

  const aiAnomaly = buildAiAnomalyExplanation(provider, anomaly, costDrivers, resources, displayCurrency, actualBillingSpend);
  const savingsOpportunities = useMemo(() => buildSavingsOpportunities(resources, costDrivers), [resources, costDrivers]);
  const governance = useMemo(() => buildTagGovernance(resources), [resources]);
  const businessAllocation = useMemo(() => buildBusinessAllocation(resources), [resources]);

  const resourceChartData = useMemo(() => {
    return resources.map((r, index) => ({
      name: r.name || `Resource ${index + 1}`,
      shortName: (r.name || `Resource ${index + 1}`).length > 18 ? `${(r.name || "").slice(0, 18)}...` : r.name || `Resource ${index + 1}`,
      type: r.resourceType || "Unknown",
      region: r.region || "N/A",
      status: r.status || "Unknown",
      cost: Number(r.estimatedMonthlyCost || 0),
      color: getResourceColor(index),
    }));
  }, [resources]);

  const actualTrend = trend.map((item) => ({ label: item.label || item.date, actualCost: convertAmount(item.cost || 0, "USD", displayCurrency, fxRates), forecastCost: null }));
  const forecastTrend = (forecast.forecastTrend || []).map((item) => ({ label: item.label, actualCost: null, forecastCost: convertAmount(item.cost || 0, "USD", displayCurrency, fxRates) }));
  const combinedTrend = [...actualTrend, ...forecastTrend];

  const cloudDistribution = useMemo(() => {
    if (isMulti) {
      return multiCloudData.map((item) => ({ name: item.cloud, value: convertAmount(item.spend, "USD", displayCurrency, fxRates), color: item.color }));
    }
    return [{ name: provider.toUpperCase(), value: actualBillingSpend, color: cloudColors[provider.toUpperCase()] || COLORS.cyan }];
  }, [isMulti, multiCloudData, provider, actualBillingSpend, displayCurrency, fxRates]);

  const comparisonData = [{ name: "Current", actual: actualBillingSpend, forecast: budgetForecast.projectedMonthEndSpend, budget: monthlyBudget }];

  useEffect(() => {
    if (!dashboardData) return;

    fetch(`${API_BASE}/api/alerts/budget`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ spend: actualBillingSpend, budget: monthlyBudget, provider, currency: displayCurrency }),
    })
      .then((res) => res.json())
      .then((data) => setBackendBudgetAlert(data))
      .catch((err) => console.error("Budget alert API failed:", err));
  }, [dashboardData, actualBillingSpend, monthlyBudget, provider, displayCurrency]);

  const handleReviewUsage = (driver) => {
    setSelectedDriver(driver);
  };

  const handleMarkAction = (id, status) => {
    setOptimizationStatus((prev) => ({ ...prev, [id]: status }));
  };

  const handleGenerateAiSummary = async () => {
    try {
      setAiLoading(true);

      if (provider !== "gcp") {
        setAiSummary({
          executiveSummary: `${provider.toUpperCase()} view is active in TanSmartX FinOps. Current spend is ${displayCurrency} ${actualBillingSpend.toFixed(4)}. Budget status is ${budgetStatus}. Forecast status: ${budgetForecast.forecastStatus}. Chargeback readiness is ${governance.score.toFixed(0)}%.`,
          riskLevel: dashboardData?.riskLevel || anomaly.level || "Low",
          recommendedActions: [
            isMulti ? `Top cloud by spend is ${topCloud?.cloud || "N/A"}.` : aiAnomaly.cause,
            aiAnomaly.impact,
            aiAnomaly.action,
            `Budget status: ${budgetStatus}. ${budgetUsedPercent.toFixed(1)}% of budget used.`,
            `Projected month-end spend is ${displayCurrency} ${budgetForecast.projectedMonthEndSpend.toFixed(2)}.`,
            `Chargeback readiness is ${governance.score.toFixed(0)}%. Improve owner, team, environment, and cost-center tags.`,
          ],
        });
        return;
      }

      const result = await fetchAiSummary(projectId, granularity);
      setAiSummary({
        ...result,
        recommendedActions: [
          ...(result.recommendedActions || []),
          `Anomaly Insight: ${aiAnomaly.cause}`,
          `Recommended Action: ${aiAnomaly.action}`,
          `Budget Status: ${budgetStatus} (${budgetUsedPercent.toFixed(1)}% used).`,
          `Forecast: Projected month-end spend is ${displayCurrency} ${budgetForecast.projectedMonthEndSpend.toFixed(2)}.`,
          `Governance: Chargeback readiness is ${governance.score.toFixed(0)}%.`,
        ],
      });
    } catch (err) {
      console.error("AI summary failed:", err);
      alert("AI summary failed. Check setup and try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAssistant = async (questionType) => {
    try {
      setAssistantLoading(true);
      setAssistantAnswer("");

      if (questionType === "anomaly") {
        setAssistantAnswer(`AI Anomaly Explanation:\n\nLikely Cause: ${aiAnomaly.cause}\n\nImpact: ${aiAnomaly.impact}\n\nRecommended Action: ${aiAnomaly.action}\n\nBudget Status: ${budgetStatus}. Current usage is ${budgetUsedPercent.toFixed(1)}% of budget.\n\nForecast: Projected month-end spend is ${displayCurrency} ${budgetForecast.projectedMonthEndSpend.toFixed(2)}.\n\nGovernance: Chargeback readiness is ${governance.score.toFixed(0)}%.`);
        return;
      }

      if (isMulti) {
        setAssistantAnswer(`Multi-cloud Executive Summary:\n\nTotal Spend: ${displayCurrency} ${actualBillingSpend.toFixed(4)}\nTop Cloud: ${topCloud?.cloud || "N/A"}\nBudget Status: ${budgetStatus}\nForecast: ${budgetForecast.forecastStatus}\nChargeback Readiness: ${governance.score.toFixed(0)}%\n\nRecommendation: Review spend by provider, validate tagging, improve owner/environment labels, and prioritize optimization on the highest-spend provider.`);
        return;
      }

      if (provider !== "gcp") {
        const spend = `${displayCurrency} ${actualBillingSpend.toFixed(4)}`;
        const msg = dashboardData?.message || `${provider.toUpperCase()} billing connected.`;
        const answers = {
          explain_cost: `Your ${provider.toUpperCase()} current detected spend is ${spend}. Budget used is ${budgetUsedPercent.toFixed(1)}%. Projected month-end spend is ${displayCurrency} ${budgetForecast.projectedMonthEndSpend.toFixed(2)}. ${msg}`,
          optimize: `${provider.toUpperCase()} optimization will become more accurate once cost drivers and usage data appear. ${aiAnomaly.action} Budget status is ${budgetStatus}. Governance score is ${governance.score.toFixed(0)}%.`,
          risk: `Your ${provider.toUpperCase()} risk level is ${dashboardData?.riskLevel || anomaly.level}. ${anomaly.message} Budget status is ${budgetStatus}. Forecast status is ${budgetForecast.forecastStatus}.`,
          summary: `${provider.toUpperCase()} is connected to TanSmartX FinOps. Current detected spend is ${spend}. Budget status is ${budgetStatus}. Forecast status is ${budgetForecast.forecastStatus}. ${msg}`,
        };
        setAssistantAnswer(answers[questionType] || answers.summary);
        return;
      }

      const result = await fetchAiAssistant(projectId, questionType, granularity);
      setAssistantAnswer(result.answer || "");
    } catch (err) {
      console.error("AI assistant failed:", err);
      setAssistantAnswer("Failed to get AI assistant response.");
    } finally {
      setAssistantLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (plan === "basic") {
      setUpgradeModal({
        title: "Unlock Executive Reports",
        eyebrow: "Standard & Enterprise Feature",
        description:
          "Generate branded executive PDF reports with financial overview, risk, anomaly insights, and recommended actions.",
        benefits: [
          "Professional executive PDF reporting",
          "Client-ready governance and budget summaries",
          "Downloadable reports for leadership reviews",
        ],
      });
      return;
    }

    try {
      setExportingPdf(true);

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 14;
      let y = 18;

      const primary = [37, 99, 235];
      const navy = [2, 6, 23];
      const slate = [100, 116, 139];
      const lightSlate = [203, 213, 225];
      const border = [226, 232, 240];
      const green = [22, 163, 74];
      const amber = [245, 158, 11];
      const red = [220, 38, 38];

      const safeText = (value) => String(value ?? "N/A");
      const cleanText = (value) =>
        safeText(value)
          .replace(/[^\x20-\x7E]/g, "")
          .replace(/\s+/g, " ")
          .trim();

      const addWrappedText = (text, x, startY, maxWidth, lineHeight = 5) => {
        const lines = pdf.splitTextToSize(cleanText(text), maxWidth);
        pdf.text(lines, x, startY);
        return startY + lines.length * lineHeight;
      };

      const addFooter = () => {
        pdf.setDrawColor(...border);
        pdf.line(margin, pageHeight - 16, pageWidth - margin, pageHeight - 16);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(...slate);
        pdf.text("Generated by Tanvoracloud | TanSmartX FinOps Platform", margin, pageHeight - 9);
        pdf.text(`Page ${pdf.internal.getNumberOfPages()}`, pageWidth - margin - 15, pageHeight - 9);
      };

      const addPageIfNeeded = (requiredSpace = 30) => {
        if (y + requiredSpace > pageHeight - 24) {
          addFooter();
          pdf.addPage();
          y = 18;
        }
      };

      const sectionTitle = (title) => {
        addPageIfNeeded(18);
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(margin, y, pageWidth - margin * 2, 10, 2, 2, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(...navy);
        pdf.text(title, margin + 4, y + 6.7);
        y += 16;
      };

      const metricCard = (x, yPos, width, title, value, subtext, color = primary) => {
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(...border);
        pdf.roundedRect(x, yPos, width, 26, 3, 3, "FD");

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...slate);
        pdf.text(cleanText(title).toUpperCase(), x + 4, yPos + 6);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(13);
        pdf.setTextColor(...color);
        pdf.text(cleanText(value), x + 4, yPos + 14);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...slate);
        pdf.text(pdf.splitTextToSize(cleanText(subtext), width - 8), x + 4, yPos + 21);
      };

      const statusColor =
        budgetStatus === "Exceeded" ? red : budgetStatus === "Warning" ? amber : green;

      const logoDataUrl = await fetch("/logo-icon.png")
        .then((res) => (res.ok ? res.blob() : null))
        .then(
          (blob) =>
            new Promise((resolve) => {
              if (!blob) return resolve(null);
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => resolve(null);
              reader.readAsDataURL(blob);
            })
        )
        .catch(() => null);

      // Cover header
      pdf.setFillColor(...navy);
      pdf.rect(0, 0, pageWidth, 52, "F");

      if (logoDataUrl) {
        try {
          pdf.addImage(logoDataUrl, "PNG", margin, 10, 22, 20);
        } catch (logoErr) {
          console.warn("PDF logo failed:", logoErr);
        }
      }

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(21);
      pdf.setTextColor(255, 255, 255);
      pdf.text("TanSmartX FinOps", margin + 30, 20);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...lightSlate);
      pdf.text("Executive Cloud Financial Control Tower Report", margin + 30, 27);
      pdf.text("by Tanvoracloud", margin + 30, 33);

      pdf.setFillColor(...primary);
      pdf.roundedRect(pageWidth - margin - 38, 13, 38, 11, 2, 2, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      pdf.text(cleanText(provider.toUpperCase()), pageWidth - margin - 31, 20.5);

      y = 64;

      // Report metadata
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(...navy);
      pdf.text("Executive Summary", margin, y);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(...slate);
      pdf.text(`Generated: ${formatDateTime()}`, margin, y + 7);
      pdf.text(`Display Currency: ${displayCurrency}`, pageWidth - margin - 55, y + 7);

      y += 18;

      // KPI cards
      const cardGap = 5;
      const cardWidth = (pageWidth - margin * 2 - cardGap * 2) / 3;
      metricCard(
        margin,
        y,
        cardWidth,
        "Total Spend",
        formatMoney(displayCurrency, actualBillingSpend, 2),
        `${provider.toUpperCase()} portfolio spend`,
        primary
      );
      metricCard(
        margin + cardWidth + cardGap,
        y,
        cardWidth,
        "Budget Health",
        `${budgetStatus}`,
        `${budgetUsedPercent.toFixed(1)}% of budget used`,
        statusColor
      );
      metricCard(
        margin + (cardWidth + cardGap) * 2,
        y,
        cardWidth,
        "Forecast",
        budgetForecast.forecastStatus,
        `Month end: ${formatMoney(displayCurrency, budgetForecast.projectedMonthEndSpend, 2)}`,
        budgetForecast.forecastStatus === "Will Exceed" ? red : green
      );

      y += 38;

      sectionTitle("1. Financial Overview");

      const overviewRows = [
        ["Provider", provider.toUpperCase()],
        ["Current Spend", formatMoney(displayCurrency, actualBillingSpend, 2)],
        ["Monthly Budget", formatMoney(displayCurrency, monthlyBudget, 2)],
        ["Budget Status", `${budgetStatus} (${budgetUsedPercent.toFixed(1)}% used)`],
        ["Projected Month End", formatMoney(displayCurrency, budgetForecast.projectedMonthEndSpend, 2)],
        ["Average Daily Burn", formatMoney(displayCurrency, budgetForecast.avgDailySpend, 4)],
        ["Days to Exceed Budget", formatDaysToExceed(budgetForecast.daysToExceed)],
        ["Top Cloud", isMulti ? topCloud?.cloud || "N/A" : provider.toUpperCase()],
      ];

      overviewRows.forEach(([label, value], index) => {
        addPageIfNeeded(9);
        const rowY = y + index * 8;
        pdf.setFillColor(index % 2 === 0 ? 255 : 248, index % 2 === 0 ? 255 : 250, index % 2 === 0 ? 255 : 252);
        pdf.rect(margin, rowY - 4, pageWidth - margin * 2, 8, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(...slate);
        pdf.text(cleanText(label), margin + 3, rowY + 1);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...navy);
        pdf.text(cleanText(value), 84, rowY + 1);
      });
      y += overviewRows.length * 8 + 8;

      sectionTitle("2. Risk, Anomaly & Governance");

      const riskRows = [
        ["Anomaly Status", anomaly.status],
        ["Anomaly Level", anomaly.level],
        ["Risk Level", dashboardData?.riskLevel || anomaly.level || "Low"],
        ["Chargeback Readiness", `${governance.score.toFixed(0)}%`],
        ["Tagged Resources", `${governance.tagged}`],
        ["Incomplete Tags", `${governance.untagged}`],
      ];

      riskRows.forEach(([label, value], index) => {
        addPageIfNeeded(8);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(...slate);
        pdf.text(cleanText(label), margin + 3, y);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...navy);
        pdf.text(cleanText(value), 84, y);
        y += 7;
      });

      y += 5;

      sectionTitle("3. Executive Insight");

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(...navy);
      y = addWrappedText(
        `Likely cause: ${aiAnomaly.cause}`,
        margin,
        y,
        pageWidth - margin * 2,
        5.2
      );
      y += 3;
      y = addWrappedText(
        `Business impact: ${aiAnomaly.impact}`,
        margin,
        y,
        pageWidth - margin * 2,
        5.2
      );
      y += 3;
      y = addWrappedText(
        `Recommended action: ${aiAnomaly.action}`,
        margin,
        y,
        pageWidth - margin * 2,
        5.2
      );
      y += 8;

      sectionTitle("4. Recommended Actions");

      const recommendations = [
        "Review highest-cost cloud/provider and validate whether the spend is expected.",
        "Confirm ownership, environment, business unit, and cost-center tags for chargeback readiness.",
        "Investigate rightsizing, scheduling, committed-use discounts, savings plans, lifecycle policies, and unused resources.",
        "Configure budget alerts and weekly FinOps review cadence for engineering and finance stakeholders.",
      ];

      recommendations.forEach((rec, index) => {
        addPageIfNeeded(14);
        pdf.setFillColor(248, 250, 252);
        pdf.setDrawColor(...border);
        pdf.roundedRect(margin, y - 5, pageWidth - margin * 2, 12, 2, 2, "FD");
        pdf.setFillColor(...primary);
        pdf.circle(margin + 5, y + 1, 2, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text(`${index + 1}`, margin + 4.1, y + 2.1);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(...navy);
        pdf.text(pdf.splitTextToSize(cleanText(rec), pageWidth - margin * 2 - 18), margin + 12, y + 2);
        y += 15;
      });

      if (costDrivers.length > 0) {
        y += 3;
        sectionTitle("5. Top Cost Drivers");

        const topDrivers = costDrivers.slice(0, 6);
        topDrivers.forEach((driver, index) => {
          addPageIfNeeded(9);
          const name = driver.service || driver.name || `Driver ${index + 1}`;
          const cost = Number(driver.cost || 0);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(9);
          pdf.setTextColor(...navy);
          pdf.text(`${index + 1}. ${cleanText(name)}`, margin, y);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(...primary);
          pdf.text(formatMoney(displayCurrency, convertAmount(cost, "USD", displayCurrency, fxRates), 4), pageWidth - margin - 48, y);
          y += 7;
        });
      }

      addFooter();
      pdf.save("TanSmartX-FinOps-Executive-Report.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("PDF export failed. Please try again.");
    } finally {
      setExportingPdf(false);
    }
  };

  if (error) return <div style={{ padding: 20, color: "red" }}>Error: {error}</div>;
  if (loadingAuth || checkingMfa) {
    return <div style={{ padding: 20, color: "white", background: "#020617", minHeight: "100vh" }}>Checking security...</div>;
  }

  if (!user) {
    return <Login />;
  }

  if (user && !userMfaEnabled) {
    return (
      <MFA
        onSetupComplete={() => {
          const currentUser = auth.currentUser;
          if (currentUser) {
            localStorage.setItem("mfa_verified", "true");
            localStorage.setItem("mfa_verified_uid", currentUser.uid);
          }
          setUserMfaEnabled(true);
          setMfaSessionVerified(true);
        }}
      />
    );
  }

  if (user && userMfaEnabled && !mfaSessionVerified) {
    return <MfaVerify
      onSuccess={() => {
        const currentUser = auth.currentUser;
        if (currentUser) {
          localStorage.setItem("mfa_verified", "true");
          localStorage.setItem("mfa_verified_uid", currentUser.uid);
        }
        setMfaSessionVerified(true);
      }}
    />;
  }
  if (page === "pricing") {
    return (
      <Pricing
        onBackToDashboard={() => setPage("dashboard")}
        onSelectPlan={handleSelectPlan}
        currentPlan={plan}
        planSaving={planSaving}
      />
    );
  }
  
  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(circle at 8% 0%, rgba(14,165,233,0.18), transparent 28%), radial-gradient(circle at 92% 4%, rgba(37,99,235,0.18), transparent 30%), linear-gradient(180deg, #06122B 0%, #020617 38%, #020617 100%)", color: "white", padding: "0 18px 34px", fontFamily: "Inter, Arial, sans-serif" }}>
      <AppPolishStyles />
      <EnterpriseSaasHero
        plan={plan}
        trialEndsAt={trialEndsAt}
        setPage={setPage}
        signOut={signOut}
        provider={provider}
        setProvider={setProvider}
        displayCurrency={displayCurrency}
        setDisplayCurrency={setDisplayCurrency}
        granularity={granularity}
        setGranularity={setGranularity}
        isAws={isAws}
        isAzure={isAzure}
        exportingPdf={exportingPdf}
        dashboardData={dashboardData}
        handleExportPdf={handleExportPdf}
        aiLoading={aiLoading}
        isStandardOrHigher={isStandardOrHigher}
        handleGenerateAiSummary={handleGenerateAiSummary}
        setUpgradeModal={setUpgradeModal}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        fxUpdatedAt={fxUpdatedAt}
        actualBillingSpend={actualBillingSpend}
        budgetStatus={budgetStatus}
        budgetForecast={budgetForecast}
      />
      <div style={{ maxWidth: "1680px", margin: "0 auto", padding: "0 18px" }}>
        {!dashboardData ? (
          <div style={{ padding: 20, color: "white" }}>Loading...</div>
        ) : (
          <>
            <EnterpriseKpiStrip
              provider={provider}
              displayCurrency={displayCurrency}
              actualBillingSpend={actualBillingSpend}
              budgetForecast={budgetForecast}
              budgetStatus={budgetStatus}
              budgetUsedPercent={budgetUsedPercent}
              governance={governance}
              savingsOpportunities={savingsOpportunities}
              fxRates={fxRates}
            />

            <div id="tx-command-center">
            <EnterpriseRealtimeCommandCenter
              provider={provider}
              displayCurrency={displayCurrency}
              actualBillingSpend={actualBillingSpend}
              monthlyBudget={monthlyBudget}
              budgetUsedPercent={budgetUsedPercent}
              budgetStatus={budgetStatus}
              budgetColor={budgetColor}
              budgetForecast={budgetForecast}
              forecastColor={forecastColor}
              anomaly={anomaly}
              combinedTrend={combinedTrend}
              cloudDistribution={cloudDistribution}
              comparisonData={comparisonData}
              governance={governance}
              savingsOpportunities={savingsOpportunities}
              fxRates={fxRates}
              granularity={granularity}
              topCloud={topCloud}
            />
            </div>

            <EnterpriseProductStorytelling
              anomaly={anomaly}
              aiAnomaly={aiAnomaly}
              governance={governance}
              savingsOpportunities={savingsOpportunities}
              displayCurrency={displayCurrency}
              fxRates={fxRates}
            />

            <EnterprisePlatformShowcase onPreview={setUpgradeModal} />

            <ExecutiveInsightPanel
              plan={plan}
              provider={provider}
              budgetStatus={budgetStatus}
              budgetForecast={budgetForecast}
              budgetUsedPercent={budgetUsedPercent}
              anomaly={anomaly}
              aiAnomaly={aiAnomaly}
              governance={governance}
              savingsOpportunities={savingsOpportunities}
              displayCurrency={displayCurrency}
              fxRates={fxRates}
              setPage={setPage}
            />

            {(activeTab === "executive" || activeTab === "finance") && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr 1.2fr", gap: "18px", marginTop: "24px" }}>
                  <DashboardWidget title="Total Savings View" badge={<Badge color={COLORS.blue}>Portfolio</Badge>} minHeight="320px">
                    <div style={{ color: "#94a3b8", fontSize: "13px", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.2px" }}>Total spend</div>
                    <div style={{ color: COLORS.blue, fontSize: "44px", fontWeight: 950, marginTop: "20px" }}>{formatMoney(displayCurrency, actualBillingSpend, 4)}</div>
                    <div style={{ marginTop: "22px", display: "grid", gap: "12px" }}>
                      <MiniMetric label="Budget" value={formatMoney(displayCurrency, monthlyBudget, 2)} color={COLORS.purple} />
                      <MiniMetric label="Budget Used" value={`${budgetUsedPercent.toFixed(1)}%`} color={budgetColor} />
                      <MiniMetric label="Forecast Risk" value={budgetForecast.forecastStatus} color={forecastColor} />
                    </div>
                  </DashboardWidget>

                  <DashboardWidget title="Spend by Cloud" badge={<Badge color={COLORS.green}>Distribution</Badge>} minHeight="320px">
                    <div style={{ height: "245px" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={cloudDistribution} dataKey="value" nameKey="name" innerRadius={55} outerRadius={88} paddingAngle={4}>
                            {cloudDistribution.map((entry, index) => <Cell key={`pie-${index}`} fill={entry.color} />)}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </DashboardWidget>

                  <DashboardWidget title="Actual vs Forecast vs Budget" badge={<Badge color={COLORS.amber}>Variance</Badge>} minHeight="320px">
                    <div style={{ height: "245px" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonData}>
                          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                          <XAxis dataKey="name" stroke="#94a3b8" />
                          <YAxis stroke="#94a3b8" />
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Legend />
                          <Bar dataKey="actual" name="Actual" fill={COLORS.blue} radius={[10, 10, 0, 0]} />
                          <Bar dataKey="forecast" name="Forecast" fill={COLORS.amber} radius={[10, 10, 0, 0]} />
                          <Bar dataKey="budget" name="Budget" fill={COLORS.purple} radius={[10, 10, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </DashboardWidget>
                </div>

                <SectionCard title="Budget Forecast" badge={<Badge color={forecastColor}>{budgetForecast.forecastStatus}</Badge>}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "18px" }}>
                    <MiniPanel label="Projected Month-End Spend" value={formatMoney(displayCurrency, budgetForecast.projectedMonthEndSpend, 2)} color={COLORS.amber} />
                    <MiniPanel label="Average Daily Spend" value={formatMoney(displayCurrency, budgetForecast.avgDailySpend, 4)} color={COLORS.blue} />
                    <MiniPanel label="Days to Exceed" value={formatDaysToExceed(budgetForecast.daysToExceed)} color={forecastColor} />
                  </div>
                  <div style={{ marginTop: "16px", color: "#cbd5e1", fontWeight: 700 }}>
                    {budgetForecast.forecastStatus === "Will Exceed" ? "🔴 Based on current spending trend, budget may exceed before month end." : "🟢 Based on current spending trend, budget is within limits."}
                  </div>
                  {backendBudgetAlert && <div style={{ color: "#94a3b8", marginTop: "10px", fontSize: "13px" }}>Backend alert engine: {backendBudgetAlert.message}</div>}
                </SectionCard>
              </>
            )}

            {(activeTab === "executive" || activeTab === "engineering") && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "18px", marginTop: "24px" }}>
                  <DashboardWidget title="Optimization Opportunities" badge={<Badge color={COLORS.green}>Prioritized</Badge>} minHeight="420px">
                    <div style={{ display: "grid", gap: "12px" }}>
                      {savingsOpportunities.slice(0, 5).map((item) => (
                        <div key={item.id} style={{ background: "#020617", border: "1px solid #334155", borderRadius: "16px", padding: "14px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
                            <div style={{ color: "white", fontWeight: 950 }}>{item.title}</div>
                            <Badge color={item.impact === "High" ? COLORS.red : item.impact === "Medium" ? COLORS.amber : COLORS.green}>{item.impact}</Badge>
                          </div>
                          <div style={{ color: "#94a3b8", marginTop: "8px", lineHeight: 1.5 }}>{item.recommendation}</div>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", marginTop: "10px", flexWrap: "wrap" }}>
                            <div style={{ color: COLORS.green, fontWeight: 950 }}>Est. savings: {formatMoney(displayCurrency, convertAmount(item.estimatedSavings, "USD", displayCurrency, fxRates), 4)}</div>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <button onClick={() => handleMarkAction(item.id, "In Review")} style={smallButton(COLORS.amber)}>Review</button>
                              <button onClick={() => handleMarkAction(item.id, "Approved")} style={smallButton(COLORS.green)}>Approve</button>
                              <Badge color={optimizationStatus[item.id] === "Approved" ? COLORS.green : optimizationStatus[item.id] === "In Review" ? COLORS.amber : COLORS.slate}>{optimizationStatus[item.id] || "Open"}</Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </DashboardWidget>

                  <DashboardWidget title="Daily Spend / Forecast Trend" badge={<Badge color={COLORS.blue}>{granularity}</Badge>} minHeight="420px">
                    {combinedTrend.length === 0 ? (
                      <div style={{ color: "#94a3b8" }}>No billing trend data available yet.</div>
                    ) : (
                      <div style={{ height: "340px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={combinedTrend}>
                            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                            <XAxis dataKey="label" stroke="#94a3b8" />
                            <YAxis stroke="#94a3b8" />
                            <Tooltip contentStyle={tooltipStyle()} />
                            <Legend />
                            <Line type="monotone" dataKey="actualCost" name="Actual Cost" stroke={COLORS.blue} strokeWidth={3} />
                            <Line type="monotone" dataKey="forecastCost" name="Forecast Cost" stroke={COLORS.amber} strokeWidth={3} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </DashboardWidget>
                </div>
              </>
            )}

            {activeTab === "finance" && (
              <SectionCard title="Business Unit Cost Allocation" badge={<Badge color={COLORS.blue}>Finance View</Badge>}>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
                  {businessAllocation.businessKPI.length === 0 ? (
                    <div style={{ color: "#94a3b8" }}>No business allocation data available yet.</div>
                  ) : (
                    businessAllocation.businessKPI.map((b, i) => (
                      <div key={i} style={{ background: "#020617", border: "1px solid #1e293b", borderRadius: "12px", padding: "12px 16px", minWidth: "160px" }}>
                        <div style={{ color: "#94a3b8", fontSize: "12px" }}>{b.name}</div>
                        <div style={{ color: COLORS.blue, fontWeight: 950 }}>{formatMoney(displayCurrency, convertAmount(b.value, "USD", displayCurrency, fxRates), 2)}</div>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px" }}>
                  <DashboardWidget title="Cost by Category" badge={<Badge color={COLORS.blue}>Category</Badge>} minHeight="320px">
                    {businessAllocation.categoryData.length === 0 ? (
                      <div style={{ color: "#94a3b8" }}>No category data available yet.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={businessAllocation.categoryData} dataKey="value" nameKey="name" outerRadius={88}>
                            {businessAllocation.categoryData.map((_, i) => <Cell key={`category-${i}`} fill={getResourceColor(i)} />)}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </DashboardWidget>

                  <DashboardWidget title="Category Trend" badge={<Badge color={COLORS.purple}>Trend</Badge>} minHeight="320px">
                    {businessAllocation.trendData.length === 0 ? (
                      <div style={{ color: "#94a3b8" }}>No trend data available yet.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={businessAllocation.trendData}>
                          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                          <XAxis dataKey="date" stroke="#94a3b8" />
                          <YAxis stroke="#94a3b8" />
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Legend />
                          {businessAllocation.categoryData.map((c, i) => <Line key={`line-${i}`} type="monotone" dataKey={c.name} stroke={getResourceColor(i)} strokeWidth={3} />)}
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </DashboardWidget>
                </div>
              </SectionCard>
            )}

            {(activeTab === "executive" || activeTab === "finance") && (
              <SectionCard title="Cost Allocation & Governance" badge={<Badge color="#60a5fa">Chargeback Ready</Badge>}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "18px" }}>
                  <DashboardWidget title="Cost by Team" badge={<Badge color={COLORS.blue}>Ownership</Badge>} minHeight="320px">
                    {governance.teamData.length === 0 ? <div style={{ color: "#94a3b8" }}>No team tag data available.</div> : (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={governance.teamData} dataKey="value" nameKey="name" outerRadius={88}>
                            {governance.teamData.map((entry, index) => <Cell key={`team-${index}`} fill={getResourceColor(index)} />)}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </DashboardWidget>

                  <DashboardWidget title="Cost by Environment" badge={<Badge color={COLORS.amber}>Prod / Dev</Badge>} minHeight="320px">
                    {governance.envData.length === 0 ? <div style={{ color: "#94a3b8" }}>No environment tag data available.</div> : (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={governance.envData}>
                          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                          <XAxis dataKey="name" stroke="#94a3b8" />
                          <YAxis stroke="#94a3b8" />
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                            {governance.envData.map((entry, index) => <Cell key={`env-${index}`} fill={getResourceColor(index)} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </DashboardWidget>

                  <DashboardWidget title="Chargeback Readiness" badge={<Badge color={COLORS.purple}>Governance</Badge>} minHeight="320px">
                    <div style={{ fontSize: "52px", fontWeight: 950, color: governance.score >= 80 ? COLORS.green : governance.score >= 50 ? COLORS.amber : COLORS.red }}>{governance.score.toFixed(0)}%</div>
                    <div style={{ marginTop: "10px", color: "#cbd5e1", lineHeight: 1.7 }}>Tagged resources: {governance.tagged}<br />Untagged / incomplete: {governance.untagged}</div>
                    <ProgressBar value={governance.score} color={governance.score >= 80 ? COLORS.green : governance.score >= 50 ? COLORS.amber : COLORS.red} />
                    <div style={{ color: "#94a3b8", marginTop: "14px", lineHeight: 1.6 }}>
                      {governance.score >= 80 ? "Strong chargeback readiness. Tagging quality is good." : governance.score >= 50 ? "Moderate readiness. Improve owner, team and environment tags." : "Low readiness. Tag governance is required before accurate chargeback."}
                    </div>
                  </DashboardWidget>
                </div>
              </SectionCard>
            )}

            {isMulti && (
              <SectionCard title="Multi-cloud Provider Breakdown" badge={<Badge color={COLORS.blue}>Global Portfolio</Badge>}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px" }}>
                  {multiCloudData.map((cloud) => {
                    const converted = convertAmount(cloud.spend, "USD", displayCurrency, fxRates);
                    const percent = actualBillingSpendRaw > 0 ? (cloud.spend / actualBillingSpendRaw) * 100 : 0;
                    return (
                      <button key={cloud.cloud} onClick={() => handleReviewUsage({ service: cloud.cloud, name: cloud.cloud, cost: cloud.spend })} style={{ textAlign: "left", background: "#020617", border: "1px solid #334155", borderRadius: "18px", padding: "16px", cursor: "pointer" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ color: "white", fontWeight: 950, fontSize: "18px" }}>{cloud.cloud}</div>
                          <Badge color={cloud.color}>{cloud.status}</Badge>
                        </div>
                        <div style={{ color: cloud.color, fontSize: "30px", fontWeight: 950, marginTop: "10px" }}>{formatMoney(displayCurrency, converted, 4)}</div>
                        <div style={{ color: "#94a3b8", marginTop: "6px" }}>{percent.toFixed(1)}% of total portfolio spend</div>
                        <div style={{ color: "#cbd5e1", marginTop: "12px", fontWeight: 800 }}>Review usage →</div>
                      </button>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            <SectionCard title="Cost Drivers Table" badge={<Badge color={COLORS.amber}>Clickable drill-down</Badge>}>
              {costDrivers.length === 0 ? (
                <div style={{ color: "#94a3b8" }}>No cost drivers yet.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ color: "#94a3b8", textAlign: "left", fontSize: "13px" }}>
                        <th style={thStyle()}>Service / Cloud</th>
                        <th style={thStyle()}>Category</th>
                        <th style={thStyle()}>Spend</th>
                        <th style={thStyle()}>Priority</th>
                        <th style={thStyle()}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costDrivers.slice(0, 10).map((d, i) => {
                        const cost = Number(d.cost || 0);
                        return (
                          <tr key={i} style={{ borderTop: "1px solid #1e293b" }}>
                            <td style={tdStyle()}>{d.service || d.name}</td>
                            <td style={tdStyle()}>{isMulti ? "Cloud Provider" : "Service"}</td>
                            <td style={{ ...tdStyle(), color: "#93C5FD", fontWeight: 950 }}>{formatMoney(displayCurrency, convertAmount(cost, "USD", displayCurrency, fxRates), 4)}</td>
                            <td style={tdStyle()}><Badge color={cost > 10 ? COLORS.red : cost > 1 ? COLORS.amber : COLORS.green}>{cost > 10 ? "High" : cost > 1 ? "Medium" : "Low"}</Badge></td>
                            <td style={tdStyle()}><button onClick={() => handleReviewUsage(d)} style={smallButton(COLORS.purple)}>Review Usage</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>

            {(activeTab === "engineering" || activeTab === "actions") && (
              <SectionCard title="Resource / Asset Breakdown Table" badge={<Badge color="#60a5fa">Engineering View</Badge>}>
                {resourceChartData.length === 0 ? (
                  <div style={{ color: "#94a3b8" }}>No resource records available yet. Connect resource discovery to populate this table.</div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ color: "#94a3b8", textAlign: "left", fontSize: "13px" }}>
                          <th style={thStyle()}>Resource</th>
                          <th style={thStyle()}>Type</th>
                          <th style={thStyle()}>Region</th>
                          <th style={thStyle()}>Status</th>
                          <th style={thStyle()}>Est. Monthly Cost</th>
                          <th style={thStyle()}>Tag Health</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resourceChartData.slice(0, 12).map((r, index) => (
                          <tr key={index} style={{ borderTop: "1px solid #1e293b" }}>
                            <td style={tdStyle()}>{r.name}</td>
                            <td style={tdStyle()}>{r.type}</td>
                            <td style={tdStyle()}>{r.region}</td>
                            <td style={tdStyle()}>{r.status}</td>
                            <td style={{ ...tdStyle(), color: "#93C5FD", fontWeight: 950 }}>{formatMoney(displayCurrency, convertAmount(r.cost, "USD", displayCurrency, fxRates), 4)}</td>
                            <td style={tdStyle()}><Badge color={index % 2 === 0 ? COLORS.green : COLORS.amber}>{index % 2 === 0 ? "Good" : "Review"}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            )}

            <SectionCard title="AI Anomaly Explanation" badge={<Badge color={COLORS.purple}>AI Insight</Badge>}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "14px" }}>
                <InsightBox title="Likely Cause" color={COLORS.blue} text={aiAnomaly.cause} />
                <InsightBox title="Business Impact" color={COLORS.amber} text={aiAnomaly.impact} />
                <InsightBox title="Recommended Action" color={COLORS.green} text={aiAnomaly.action} />
              </div>
            </SectionCard>

            <SectionCard title="Cost Spike Alert" badge={<Badge color={anomaly.level === "High" ? COLORS.red : anomaly.level === "Medium" ? COLORS.amber : COLORS.green}>{anomaly.status}</Badge>}>
              <div style={{ padding: "18px", borderRadius: "18px", background: anomaly.level === "High" ? "#450a0a" : anomaly.level === "Medium" ? "#422006" : "#052e2b", border: anomaly.level === "High" ? "1px solid #ef4444" : anomaly.level === "Medium" ? "1px solid #f59e0b" : "1px solid #14b8a6" }}>
                <div style={{ fontSize: "24px", fontWeight: 900 }}>{anomaly.status}</div>
                <div style={{ color: "#cbd5e1", marginTop: "8px", lineHeight: 1.6 }}>{anomaly.message}</div>
                <div style={{ marginTop: "12px", color: "#93C5FD", fontWeight: 900 }}>Spike Change: {anomaly.spikePercent.toFixed(1)}%</div>
              </div>
            </SectionCard>

            {aiSummary && (
              <SectionCard title="AI Executive Summary" badge={<Badge color={COLORS.purple}>Generated</Badge>}>
                <div style={{ color: "#cbd5e1", lineHeight: 1.7, marginBottom: "14px" }}>{aiSummary.executiveSummary}</div>
                <div style={{ color: "#93C5FD", fontWeight: 900, marginBottom: "12px" }}>Risk Level: {aiSummary.riskLevel}</div>
                {(aiSummary.recommendedActions || []).map((item, i) => <div key={i} style={{ color: "#94a3b8", paddingBottom: "8px", lineHeight: 1.6 }}>{i + 1}. {item}</div>)}
              </SectionCard>
            )}


            {activeTab === "tbm" && (
              <>
                <SectionCard title="Technology Business Management" badge={<Badge color={COLORS.purple}>TBM Lite</Badge>}>
                  {tbmLoading ? (
                    <div style={{ color: "#94a3b8" }}>Loading TBM data...</div>
                  ) : tbmError ? (
                    
<div
  style={{
    color: "#CBD5E1",
    background: "radial-gradient(circle at 0% 0%, rgba(124,58,237,0.14), transparent 28%), rgba(15,23,42,0.72)",
    border: "1px solid rgba(168,85,247,0.24)",
    borderRadius: "20px",
    padding: "20px",
    lineHeight: 1.65,
  }}
>
  <div style={{ color: "#FFFFFF", fontWeight: 950, fontSize: "18px", marginBottom: "8px" }}>
    TBM Lite Preview Mode
  </div>
  <div style={{ color: "#CBD5E1" }}>
    TBM API is connected to Cloud Run. If this preview appears, verify BigQuery billing table access, CORS, and the billing_export dataset/table prefix.
  </div>
  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "14px" }}>
    <button
      onClick={() => alert("Business Unit Mapping connects GCP billing cost to project, team, owner, business unit, and chargeback views.")}
      style={{ ...integrationPill(), cursor: "pointer" }}
    >
      Business Unit Mapping
    </button>
    <button
      onClick={() => alert("Application Costing groups cloud cost by application/service using labels, project names, and billing metadata.")}
      style={{ ...integrationPill(), cursor: "pointer" }}
    >
      Application Costing
    </button>
    <button
      onClick={() => alert("Unmapped Cost Analysis identifies billing spend without labels or ownership mapping so teams can improve governance.")}
      style={{ ...integrationPill(), cursor: "pointer" }}
    >
      Unmapped Cost Analysis
    </button>
  </div>
</div>

                  ) : !tbmData ? (
                    <div style={{ color: "#94a3b8" }}>No TBM data available yet.</div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "18px" }}>
                        <MiniPanel label="Total TBM Cost" value={formatMoney(displayCurrency, convertAmount(tbmData.totalCost, "USD", displayCurrency, fxRates), 2)} color={COLORS.blue} />
                        <MiniPanel label="Mapped Cost" value={formatMoney(displayCurrency, convertAmount(tbmData.mappedCost, "USD", displayCurrency, fxRates), 2)} color={COLORS.green} />
                        <MiniPanel label="Unmapped Cost" value={formatMoney(displayCurrency, convertAmount(tbmData.unmappedCost, "USD", displayCurrency, fxRates), 2)} color={COLORS.amber} />
                        <MiniPanel label="Mapping Coverage" value={`${Number(tbmData.mappingCoverage ?? tbmData.mappedPercent ?? 0).toFixed(2)}%`} color={COLORS.purple} />
                      </div>

                      <div style={{ marginTop: "18px", color: "#cbd5e1", lineHeight: 1.7 }}>
                        {tbmData.message || "TBM-lite engine is active."} This view translates raw cloud services into business language for finance, CIO, and business owners.
                      </div>
                    </>
                  )}
                </SectionCard>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "18px", marginTop: "24px" }}>
                  <DashboardWidget title="Cost by Business Unit" badge={<Badge color={COLORS.blue}>Business View</Badge>} minHeight="360px">
                    {tbmBusinessUnits.length === 0 ? (
                      <div style={{ color: "#94a3b8" }}>No business unit cost data available.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={290}>
                        <BarChart data={tbmBusinessUnits}>
                          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                          <XAxis dataKey="name" stroke="#94a3b8" />
                          <YAxis stroke="#94a3b8" />
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Bar dataKey="cost" name="Cost" radius={[10, 10, 0, 0]}>
                            {tbmBusinessUnits.map((_, index) => <Cell key={`tbm-bu-${index}`} fill={getResourceColor(index)} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </DashboardWidget>

                  <DashboardWidget title="Cost by Application" badge={<Badge color={COLORS.green}>Application View</Badge>} minHeight="360px">
                    {tbmApplications.length === 0 ? (
                      <div style={{ color: "#94a3b8" }}>No application cost data available.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={290}>
                        <PieChart>
                          <Pie data={tbmApplications} dataKey="cost" nameKey="name" innerRadius={60} outerRadius={105} paddingAngle={4}>
                            {tbmApplications.map((_, index) => <Cell key={`tbm-app-${index}`} fill={getResourceColor(index)} />)}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle()} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </DashboardWidget>
                </div>

                <SectionCard title="Unmapped Cost Governance" badge={<Badge color={COLORS.amber}>Allocation Gaps</Badge>}>
                  {!tbmUnmapped ? (
                    <div style={{ color: "#94a3b8" }}>No unmapped data available.</div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "18px" }}>
                        <MiniPanel label="Unmapped Cost" value={formatMoney(displayCurrency, convertAmount(tbmUnmapped.unmappedCost, "USD", displayCurrency, fxRates), 2)} color={COLORS.amber} />
                        <MiniPanel label="Unmapped %" value={`${Number(tbmUnmapped.unmappedPercent || 0).toFixed(2)}%`} color={COLORS.red} />
                      </div>

                      {(tbmUnmapped.resources || []).length === 0 ? (
                        <div style={{ color: "#94a3b8" }}>Excellent — no unmapped resources found.</div>
                      ) : (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ color: "#94a3b8", textAlign: "left", fontSize: "13px" }}>
                                <th style={thStyle()}>Provider</th>
                                <th style={thStyle()}>Service</th>
                                <th style={thStyle()}>Resource</th>
                                <th style={thStyle()}>Region</th>
                                <th style={thStyle()}>Cost</th>
                                <th style={thStyle()}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(tbmUnmapped.resources || []).slice(0, 8).map((item, index) => (
                                <tr key={`unmapped-${index}`} style={{ borderTop: "1px solid #1e293b" }}>
                                  <td style={tdStyle()}>{item.provider || "N/A"}</td>
                                  <td style={tdStyle()}>{item.service || "N/A"}</td>
                                  <td style={tdStyle()}>{item.resource_name || "N/A"}</td>
                                  <td style={tdStyle()}>{item.region || "N/A"}</td>
                                  <td style={{ ...tdStyle(), color: "#FBBF24", fontWeight: 950 }}>{formatMoney(displayCurrency, convertAmount(item.cost, "USD", displayCurrency, fxRates), 2)}</td>
                                  <td style={tdStyle()}><Badge color={COLORS.amber}>Create Mapping</Badge></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </SectionCard>
              </>
            )}

            <div id="tx-ai-section">
              <GuidedAssistant assistantLoading={assistantLoading} assistantAnswer={assistantAnswer} handleAssistant={handleAssistant} />
            </div>
          </>
        )}
      </div>

      <UsageDrawer selectedDriver={selectedDriver} onClose={() => setSelectedDriver(null)} displayCurrency={displayCurrency} fxRates={fxRates} resources={resources} trend={trend} provider={provider} />
      <UpgradeModal
        modal={upgradeModal}
        onClose={() => setUpgradeModal(null)}
        onUpgrade={() => {
          setUpgradeModal(null);
          setPage("pricing");
        }}
      />
      <TaniAssistant setPage={setPage} setUpgradeModal={setUpgradeModal} />
    </div>
  );
}



function AppPolishStyles() {
  return (
    <style>{`
      .tx-card-hover, .tx-card-soft, .tx-button-hover, .tx-clickable {
        transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease, background 220ms ease, opacity 220ms ease;
      }
      .tx-card-hover:hover {
        transform: translateY(-4px);
        box-shadow: 0 24px 70px rgba(0,0,0,0.34), 0 0 0 1px rgba(56,189,248,0.12) inset !important;
        border-color: rgba(125,211,252,0.34) !important;
      }
      .tx-card-soft:hover {
        border-color: rgba(125,211,252,0.25) !important;
      }
      .tx-button-hover:hover {
        transform: translateY(-2px);
        opacity: 0.96;
        box-shadow: 0 16px 40px rgba(37,99,235,0.22) !important;
      }
      .tx-clickable { cursor: pointer; }
      .tx-dot {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        display: inline-block;
        background: #2563EB;
        animation: txPulse 1s infinite ease-in-out;
      }
      .tx-dot2 { animation-delay: 0.16s; }
      .tx-dot3 { animation-delay: 0.32s; }
      @keyframes txPulse {
        0%, 80%, 100% { transform: scale(0.7); opacity: 0.45; }
        40% { transform: scale(1); opacity: 1; }
      }
      @media (max-width: 900px) {
        .recharts-wrapper { max-width: 100% !important; }
      }
    `}</style>
  );
}

function UpgradeModal({ modal, onClose, onUpgrade }) {
  if (!modal) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2500,
        background:
          "radial-gradient(circle at top, rgba(37,99,235,0.18), transparent 34%), rgba(2,6,23,0.84)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backdropFilter: "blur(10px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(520px, 94vw)",
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))",
          border: "1px solid rgba(148,163,184,0.24)",
          borderRadius: "26px",
          boxShadow: "0 32px 90px rgba(0,0,0,0.48)",
          padding: "30px",
          position: "relative",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            position: "absolute",
            top: "-80px",
            right: "-80px",
            width: "190px",
            height: "190px",
            background:
              "radial-gradient(circle, rgba(37,99,235,0.26), transparent 70%)",
          }}
        />

        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "flex-start", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src="/logo-icon.png"
              alt="Tanvoracloud"
              style={{ height: "42px", width: "auto", objectFit: "contain" }}
            />
            <div>
              <div style={{ color: "white", fontWeight: 950, fontSize: "17px", lineHeight: 1 }}>
                TanSmartX FinOps
              </div>
              <div style={{ color: "#94A3B8", fontSize: "12px", fontWeight: 800, marginTop: "4px" }}>
                by Tanvoracloud
              </div>
            </div>
          </div>
          <button onClick={onClose} style={closeButton()} aria-label="Close upgrade modal">×</button>
        </div>

        <div style={{ position: "relative", zIndex: 1, marginTop: "24px" }}>
          <div
            style={{
              display: "inline-flex",
              padding: "7px 12px",
              borderRadius: "999px",
              background: "rgba(37,99,235,0.18)",
              border: "1px solid rgba(37,99,235,0.38)",
              color: "#BFDBFE",
              fontSize: "12px",
              fontWeight: 950,
              marginBottom: "14px",
            }}
          >
            {modal.eyebrow || "Premium Feature"}
          </div>

          <h2 style={{ color: "white", margin: "0 0 10px", fontSize: "30px", letterSpacing: "-0.8px", lineHeight: 1.08 }}>
            {modal.title || "Unlock Premium Features"}
          </h2>

          <p style={{ color: "#CBD5E1", fontSize: "15px", lineHeight: 1.7, margin: "0 0 18px" }}>
            {modal.description || "Upgrade to Standard or Enterprise to access advanced FinOps capabilities."}
          </p>

          <div style={{ display: "grid", gap: "10px", margin: "20px 0 24px" }}>
            {(modal.benefits || ["Executive PDF reports", "AI insights and recommendations", "Multi-cloud analytics"]).map((benefit) => (
              <div
                key={benefit}
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  background: "rgba(2,6,23,0.76)",
                  border: "1px solid rgba(148,163,184,0.16)",
                  borderRadius: "14px",
                  padding: "12px",
                  color: "#E2E8F0",
                  fontWeight: 800,
                  fontSize: "14px",
                }}
              >
                <span style={{ color: "#93C5FD", fontWeight: 950 }}>✓</span>
                <span>{benefit}</span>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gap: "10px" }}>
            <button
              onClick={onUpgrade}
              style={{
                width: "100%",
                border: "none",
                borderRadius: "16px",
                padding: "15px 18px",
                background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                color: "white",
                fontSize: "16px",
                fontWeight: 950,
                cursor: "pointer",
                boxShadow: "0 18px 36px rgba(37,99,235,0.28)",
              }}
            >
              {modal.ctaLabel || "View Plans"}
            </button>
            <button
              onClick={onClose}
              style={{
                width: "100%",
                border: "1px solid rgba(148,163,184,0.18)",
                borderRadius: "16px",
                padding: "13px 18px",
                background: "transparent",
                color: "#94A3B8",
                fontSize: "14px",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              {modal.secondaryLabel || "Continue with Basic"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionRow({ title, description, impact }) {
  return (
    <div style={{ background: "#020617", border: "1px solid #334155", borderRadius: "16px", padding: "14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
        <div style={{ color: "white", fontWeight: 950 }}>{title}</div>
        <Badge color={impact === "High" ? COLORS.red : impact === "Medium" ? COLORS.amber : COLORS.green}>{impact}</Badge>
      </div>
      <div style={{ color: "#94a3b8", marginTop: "8px", lineHeight: 1.5 }}>{description}</div>
    </div>
  );
}

function ProgressBar({ value, color }) {
  return (
    <div style={{ marginTop: "18px", height: "16px", background: "#020617", borderRadius: "999px", border: "1px solid #1e293b", overflow: "hidden" }}>
      <div style={{ width: `${Math.min(Math.max(value, 0), 100)}%`, height: "100%", background: color, boxShadow: `0 0 14px ${color}`, transition: "width 0.5s ease" }} />
    </div>
  );
}

function inputStyle(disabled = false) {
  return {
    background: "#0F172A",
    color: "#E5E7EB",
    border: "1px solid rgba(148,163,184,0.25)",
    borderRadius: "14px",
    padding: "12px 16px",
    minWidth: "150px",
    opacity: disabled ? 0.55 : 1,
  };
}



function EnterpriseKpiStrip({ provider, displayCurrency, actualBillingSpend, budgetForecast, budgetStatus, budgetUsedPercent, governance, savingsOpportunities, fxRates }) {
  const savingsTotal = savingsOpportunities.reduce((sum, item) => sum + Number(item.estimatedSavings || 0), 0);
  const savings = convertAmount(savingsTotal, "USD", displayCurrency, fxRates);
  const budgetColor = budgetStatus === "Exceeded" ? COLORS.red : budgetStatus === "Warning" ? COLORS.amber : COLORS.green;
  const forecastColor = budgetForecast.forecastStatus === "Will Exceed" ? COLORS.red : COLORS.green;
  const chargebackColor = governance.score >= 80 ? COLORS.green : governance.score >= 50 ? COLORS.amber : COLORS.red;

  const cards = [
    {
      label: provider === "multi" ? "Total Cloud Spend" : `${provider.toUpperCase()} Actual Spend`,
      value: formatMoney(displayCurrency, actualBillingSpend, 4),
      meta: "Live portfolio spend",
      color: COLORS.cyan,
      trend: "Financial control point",
    },
    {
      label: "Projected Month-End",
      value: formatMoney(displayCurrency, budgetForecast.projectedMonthEndSpend, 2),
      meta: `Avg daily burn: ${formatMoney(displayCurrency, budgetForecast.avgDailySpend, 4)}`,
      color: forecastColor,
      trend: budgetForecast.forecastStatus,
    },
    {
      label: "Budget Health",
      value: budgetStatus,
      meta: `${budgetUsedPercent.toFixed(1)}% of budget used`,
      color: budgetColor,
      trend: budgetStatus === "Healthy" ? "Within control" : "Needs attention",
    },
    {
      label: "Chargeback Readiness",
      value: `${governance.score.toFixed(0)}%`,
      meta: `${governance.tagged} tagged / ${governance.untagged} incomplete`,
      color: chargebackColor,
      trend: governance.score >= 80 ? "Audit friendly" : "Improve tags",
    },
    {
      label: "Optimization Pipeline",
      value: formatMoney(displayCurrency, savings, 2),
      meta: `${savingsOpportunities.length} actions available`,
      color: COLORS.green,
      trend: "Savings opportunity",
    },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(245px, 1fr))", gap: "18px" }}>
      {cards.map((card) => (
        <div key={card.label} style={enterpriseKpiCard(card.color)}>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center" }}>
              <div style={{ color: "#94A3B8", fontSize: "12px", fontWeight: 950, textTransform: "uppercase", letterSpacing: "1.3px" }}>{card.label}</div>
              <span style={{ width: "10px", height: "10px", borderRadius: "999px", background: card.color, boxShadow: `0 0 18px ${card.color}` }} />
            </div>
            <div style={{ color: "white", fontSize: "36px", lineHeight: 1.05, fontWeight: 950, letterSpacing: "-1px", marginTop: "16px" }}>{card.value}</div>
            <div style={{ color: "#CBD5E1", fontSize: "14px", lineHeight: 1.5, marginTop: "12px" }}>{card.meta}</div>
            <div style={{ display: "inline-flex", marginTop: "14px", padding: "7px 10px", borderRadius: "999px", background: `${card.color}18`, border: `1px solid ${card.color}44`, color: card.color, fontSize: "12px", fontWeight: 900 }}>
              {card.trend}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ExecutiveInsightPanel({ plan, provider, budgetStatus, budgetForecast, budgetUsedPercent, anomaly, aiAnomaly, governance, savingsOpportunities, displayCurrency, fxRates, setPage }) {
  const savingsTotal = savingsOpportunities.reduce((sum, item) => sum + Number(item.estimatedSavings || 0), 0);
  const convertedSavings = convertAmount(savingsTotal, "USD", displayCurrency, fxRates);
  const highestOpportunity = savingsOpportunities[0];
  const isHealthy = budgetStatus === "Healthy" && anomaly.level !== "High";

  const insights = [
    {
      title: isHealthy ? "Executive Position" : "Executive Attention Required",
      text: isHealthy
        ? `Spend posture is controlled. Budget utilization is ${budgetUsedPercent.toFixed(1)}% and forecast is ${budgetForecast.forecastStatus}.`
        : `Budget status is ${budgetStatus}. Forecast is ${budgetForecast.forecastStatus}; prioritize review before month-end.`,
      color: isHealthy ? COLORS.green : COLORS.amber,
    },
    {
      title: "Anomaly Narrative",
      text: aiAnomaly.cause,
      color: anomaly.level === "High" ? COLORS.red : COLORS.blue,
    },
    {
      title: "Optimization Focus",
      text: highestOpportunity
        ? `${highestOpportunity.title}. Estimated pipeline value is ${formatMoney(displayCurrency, convertedSavings, 2)}.`
        : "No optimization opportunity detected yet. Connect deeper resource metadata to improve recommendations.",
      color: COLORS.green,
    },
    {
      title: "Governance Readiness",
      text: `Chargeback readiness is ${governance.score.toFixed(0)}%. Improve owner, environment, team, and cost-center tags for better allocation.`,
      color: governance.score >= 80 ? COLORS.green : COLORS.purple,
    },
  ];

  return (
    <div style={{ ...cardStyle, padding: "24px", marginTop: "24px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "-80px", right: "-90px", width: "220px", height: "220px", background: "radial-gradient(circle, rgba(37,99,235,0.22), transparent 68%)" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "18px", flexWrap: "wrap", position: "relative", zIndex: 1 }}>
        <div>
          <Badge color={COLORS.purple}>Executive Intelligence</Badge>
          <h2 style={{ margin: "12px 0 8px", color: "white", fontSize: "25px", letterSpacing: "-0.5px" }}>Management Summary & Next Best Actions</h2>
          <p style={{ margin: 0, color: "#94A3B8", maxWidth: "820px", lineHeight: 1.65 }}>
            A board-ready view of cloud spend posture, forecast risk, governance quality, and optimization priority.
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button onClick={() => setPage("pricing")} style={upgradeButtonStyle(plan)}>
            {plan === "enterprise" ? "Enterprise Active" : "Upgrade Plan"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "14px", marginTop: "22px", position: "relative", zIndex: 1 }}>
        {insights.map((item) => (
          <div key={item.title} style={enterpriseInsightCard(item.color)}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <span style={{ width: "9px", height: "9px", borderRadius: "999px", background: item.color, boxShadow: `0 0 16px ${item.color}` }} />
              <div style={{ color: "white", fontWeight: 950 }}>{item.title}</div>
            </div>
            <div style={{ color: "#CBD5E1", lineHeight: 1.65, fontSize: "14px" }}>{item.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function enterpriseKpiCard(color) {
  return {
    ...cardStyle,
    padding: "24px",
    minHeight: "178px",
    position: "relative",
    overflow: "hidden",
    background: `linear-gradient(180deg, rgba(15,23,42,0.99), rgba(8,13,24,0.99)), radial-gradient(circle at top right, ${color}22, transparent 58%)`,
  };
}

function enterpriseInsightCard(color) {
  return {
    background: "rgba(2,6,23,0.78)",
    border: `1px solid ${color}33`,
    borderRadius: "18px",
    padding: "18px",
    boxShadow: "0 16px 36px rgba(0,0,0,0.18)",
  };
}

function upgradeButtonStyle(plan) {
  return {
    background: plan === "enterprise" ? "rgba(22,163,74,0.18)" : "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: plan === "enterprise" ? "#BBF7D0" : "white",
    border: plan === "enterprise" ? "1px solid rgba(22,163,74,0.35)" : "1px solid rgba(255,255,255,0.10)",
    borderRadius: "999px",
    padding: "12px 16px",
    fontWeight: 950,
    cursor: "pointer",
  };
}

function planBadgeStyle(currentPlan) {
  const color = currentPlan === "enterprise" ? "#A78BFA" : currentPlan === "standard" ? "#93C5FD" : "#CBD5E1";
  return {
    display: "inline-flex",
    alignItems: "center",
    border: '1px solid ' + color + '55',
    background: color + '18',
    color,
    borderRadius: "999px",
    padding: "10px 14px",
    fontSize: "12px",
    fontWeight: 950,
    whiteSpace: "nowrap",
  };
}

function topNavButton(active = false) {
  return {
    background: active ? "rgba(37,99,235,0.22)" : "transparent",
    border: active ? "1px solid rgba(37,99,235,0.45)" : "1px solid rgba(148,163,184,0.18)",
    color: active ? "#BFDBFE" : "#CBD5E1",
    borderRadius: "999px",
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  };
}

function primaryButton(disabled = false) {
  return {
    background: disabled ? "#334155" : COLORS.blue,
    color: "white",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "12px",
    padding: "12px 18px",
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

function secondaryButton(disabled = false) {
  return {
    background: "#0F172A",
    color: disabled ? "#64748b" : "#E5E7EB",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: "12px",
    padding: "12px 18px",
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

function smallButton(color) {
  return {
    background: color,
    color: "white",
    border: "none",
    borderRadius: "10px",
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 900,
    whiteSpace: "nowrap",
  };
}

function closeButton() {
  return {
    background: "#0F172A",
    color: "white",
    border: "1px solid #334155",
    borderRadius: "12px",
    width: "42px",
    height: "42px",
    cursor: "pointer",
    fontSize: "26px",
    lineHeight: 1,
  };
}

function tooltipStyle() {
  return { backgroundColor: "#020617", border: "1px solid #334155", borderRadius: "14px", color: "white" };
}

function thStyle() {
  return { padding: "12px", borderBottom: "1px solid #334155", whiteSpace: "nowrap" };
}

function tdStyle() {
  return { padding: "12px", color: "#cbd5e1", whiteSpace: "nowrap" };
}

function MiniMetric({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", background: "#020617", border: "1px solid #1e293b", padding: "12px", borderRadius: "14px", gap: "10px" }}>
      <span style={{ color: "#94a3b8", fontWeight: 800 }}>{label}</span>
      <span style={{ color, fontWeight: 950 }}>{value}</span>
    </div>
  );
}

function MiniPanel({ label, value, color }) {
  return (
    <div style={{ background: "#020617", border: "1px solid #334155", borderRadius: "18px", padding: "16px" }}>
      <div style={{ color: "#94a3b8", fontSize: "13px", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{label}</div>
      <div style={{ color, fontSize: "26px", fontWeight: 950, marginTop: "10px" }}>{value}</div>
    </div>
  );
}

function InsightBox({ title, color, text }) {
  return (
    <div style={{ background: "#020617", border: "1px solid #334155", borderRadius: "18px", padding: "16px" }}>
      <div style={{ color, fontWeight: 900, marginBottom: "8px" }}>{title}</div>
      <div style={{ color: "#cbd5e1", lineHeight: 1.65 }}>{text}</div>
    </div>
  );
}
